import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useParams } from "react-router-dom";
import { Form, Input, Row, Col, Switch, Typography, Divider, Radio, Checkbox, App } from "antd";
import { DownOutlined, PlusOutlined } from "@ant-design/icons";
import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import { ProductSelectInput } from "../../components/ProductSelectInput";
import type { OrderFinancialVO, ChooseProduct } from "./orders";
import { getProductCompanyList, useMetadataStore, getProductInfo, calculatePremiumCapacity, getSelectMetadataByCode, type PremiumCapacityResponse, useConfigStore } from "@ifa/utils";
import type { ProductCompanyListVO, ProductInfoVO } from "@ifa/utils";
import { SelectInput } from "../../components/form/SelectInput";
import { AmountInput } from "../../components/form/AmountInput";
import { useFormConfig } from "../../hooks/useFormConfig";
import styles from "./FinancialForm.module.css";
import { isZuuFinancialStepVariant } from "../order-fna/fnaVariant.js";
import { getZuuFnaQuestionAliases, getZuuFnaQuestionValueFromList, toZuuFnaStoredQuestionValue } from "../order-fna/zuuFnaFieldMap";
import { deriveZuuStep2Draft } from "../order-fna/zuuStep2Draft";
import { GenericFnaForm } from "../order-fna/GenericFnaForm";
import {
  ZuuFnaForm,
  type ZuuFinancialAnalysisSectionProps,
  type ZuuOnlyProvideOneProductSectionProps,
  type ZuuProductSelectionSectionProps,
  type ZuuPremiumFinancingSectionProps,
  type ZuuSuggestionsSectionProps,
} from "../order-fna/ZuuFnaForm";
import { ZuuFinancialInfoStep2Section } from "../order-fna/ZuuFinancialInfoStep2Section";
import { shouldShowZuuRetirementFields, validateZuuFinancialInfoStep2, validateZuuPremiumFinancing } from "../order-fna/zuuValidation";
import TextArea from "antd/lib/input/TextArea";
import type { OrderVO } from "../OrderForms/orders";
const { Title, Text } = Typography;
const cx = (...classNames: Array<string | false | null | undefined>) => classNames.filter(Boolean).join(" ");

interface FinancialFormProps {
  data: OrderFinancialVO;
  onChange: (data: OrderFinancialVO) => void;
  isFinancialInfoRequired?: boolean;
  readOnly?: boolean;
  detailDisplay?: boolean;
  onOpenSelector?: (context: { type: string; index: number }, productType?: string) => void;
  activeSection?: "financial-info" | "fna";
  orderData: OrderVO;
  zuuReferenceLatestFna?: {
    orderNo?: string;
    signTime?: string;
    onReference: () => void;
  } | null;
}

export interface FinancialFormRef {
  validate: () => Promise<any>;
}

export const FinancialForm = forwardRef<FinancialFormRef, FinancialFormProps>(
  ({ data = {}, onChange, isFinancialInfoRequired, readOnly = false, detailDisplay = false, onOpenSelector, activeSection = "financial-info", orderData, zuuReferenceLatestFna = null }, ref) => {
    const [form] = Form.useForm();
    const { orderId } = useParams();
    const { formRequiredConfig } = useMetadataStore;
    const { applyDefaultValues } = useFormConfig("FinancialForm", formRequiredConfig, data);
    const { message } = App.useApp();
    const productSectionRef = React.useRef<HTMLDivElement>(null);

    // Track product validation status
    const [productValidation, setProductValidation] = useState<Record<number, { paymentTermValid: boolean; benefitTermValid: boolean }>>({});

    // 保费承担能力数据（从 API 获取）
    const [premiumCapacity, setPremiumCapacity] = useState<PremiumCapacityResponse | null>(null);
    const brandId = useConfigStore((state) => state.brandId);
    const tenantCode = useConfigStore((state) => state.tenantProfile?.tenantCode);
    const isZuuFinancialStepMode = isZuuFinancialStepVariant({
      brandId,
      tenantCode,
    });
    const FnaFormComponent = isZuuFinancialStepMode ? ZuuFnaForm : GenericFnaForm;

    // Define helper functions before useImperativeHandle
    const getQuestionValue = (section: "financialAnalysis" | "transferInsuranceDeclaration" | "suggestionsAndReasons", questionKey: string, type: "RADIO" | "CHECKBOX" | "INPUT" = "RADIO") => {
      const list = data[section] || [];
      return getZuuFnaQuestionValueFromList(list, questionKey, type);
    };

    // 计算年龄（根据出生日期）
    const calculateAge = (birthDate: string | undefined): number | null => {
      if (!birthDate) return null;

      const birth = new Date(birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();

      // 如果还没到生日，年龄减1
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }

      return age;
    };

    // 判断是否显示问题 4.2
    const shouldShowQuestion42 = (): boolean => {
      const intendedRetirementAge = orderData?.policyholder?.work?.intendedRetirementAge;
      const willingPremiumPaymentPeriodRange = getQuestionValue("financialAnalysis", "willingPremiumPaymentPeriodRange", "RADIO");

      // 如果预计退休年龄为空，则固定隐藏问题 4.2
      if (!intendedRetirementAge) {
        return false;
      }

      // 条件1：如果预计退休年龄选择了「已退休」或「其他」，则显示问题 4.2
      if (intendedRetirementAge === "RETIRED" || intendedRetirementAge === "OTHERS") {
        return true;
      }

      // 如果没有选择问题 4.1，则不显示问题 4.2
      if (!willingPremiumPaymentPeriodRange) {
        return false;
      }

      // 条件2：如果预计退休年龄选择了具体年龄，且问题 4.1 选择了 E 或 F，则显示问题 4.2
      if (willingPremiumPaymentPeriodRange === "E" || willingPremiumPaymentPeriodRange === "F") {
        return true;
      }

      // 条件3：如果预计退休年龄选择了具体年龄，且问题 4.1 选择了 A/B/C/D
      // 且该选项的最大年期 + 当前投保人年龄 > 预计退休年龄，则显示问题 4.2
      if (["A", "B", "C", "D"].includes(willingPremiumPaymentPeriodRange)) {
        // 获取当前投保人年龄
        const currentAge = calculateAge(orderData?.policyholder?.basic?.birthDate);
        if (currentAge === null) return false;

        // 从元数据中获取预计退休年龄的具体数值
        const retirementAgeMetadata = getSelectMetadataByCode("INTENDED_RETIREMENT_AGE_OPTION");
        const retirementAgeItem = retirementAgeMetadata?.items?.find((item) => item.key === intendedRetirementAge);

        if (!retirementAgeItem?.key) return false;

        // 从 key 中提取数字（例如 "AGE_45" -> 45）
        const ageMatch = retirementAgeItem.key.match(/\d+/);
        const retirementAge = ageMatch ? parseInt(ageMatch[0], 10) : null;

        if (retirementAge === null || isNaN(retirementAge)) return false;

        // 获取选项的最大年期
        const maxYearsMap: Record<string, number> = {
          A: 5, // 2-5年
          B: 10, // 6-10年
          C: 15, // 11-15年
          D: 20, // 16-20年
        };
        const maxYears = maxYearsMap[willingPremiumPaymentPeriodRange];

        // 判断：最大年期 + 当前年龄 > 预计退休年龄
        if (currentAge + maxYears > retirementAge) {
          return true;
        }
      }

      return false;
    };

    useImperativeHandle(ref, () => ({
      validate: async () => {
        // 如果没有开启财务资料，不需要校验
        if (!isFinancialInfoRequired) {
          return Promise.resolve();
        }

        // ZUU 财务基本信息步骤也需要校验
        if (activeSection === "financial-info" && isZuuFinancialStepMode) {
          const errors = validateZuuFinancialInfoStep2(data, {
            isFinancialInfoRequired,
            showRetirementFields: shouldShowZuuRetirementFields(orderData?.policyholder),
          });
          if (errors.length > 0) {
            form.setFields(errors);
            form.scrollToField(errors[0].name);
            throw { errorFields: errors };
          }
          return Promise.resolve();
        }

        // 只在 FNA 步骤才进行校验
        if (activeSection === "fna") {
          const errors = [];

          // 校验表单字段
          try {
            await form.validateFields();
          } catch (error: any) {
            if (error.errorFields) {
              errors.push(...error.errorFields);
            }
          }

          // 检查问题 4.1 的输入框（当选择 G 时）
          if (getQuestionValue("financialAnalysis", "willingPremiumPaymentPeriodRange", "RADIO") === "G") {
            const singlePaymentLimit = getQuestionValue("financialAnalysis", "singlePremiumPaymentLimit", "INPUT");
            if (!singlePaymentLimit || singlePaymentLimit === 0) {
              errors.push({
                name: ["singlePremiumPaymentLimit"],
                errors: ["請輸入金額"],
              });
            }
          }

          errors.push(...validateZuuPremiumFinancing(data));

          // 需求 2.1：校验所有产品计划不能都为空
          const allProducts = data.chooseProducts || [];
          const hasAnyProduct = allProducts.some((p) => p.productCode);
          if (!hasAnyProduct) {
            errors.push({
              name: ["chooseProducts"],
              errors: ["介紹的產品計劃不能為空"],
            });
          }

          // 需求 3.1：校验至少要勾选一个产品
          const selectedProducts = allProducts.filter((p) => p.choose);
          if (hasAnyProduct && selectedProducts.length === 0) {
            errors.push({
              name: ["chooseProducts"],
              errors: ["請勾選一款介紹的產品投保"],
            });
          }

          // 需求 1.1：校验只能提供一家保险公司时必须填写原因
          const productsWithCompany = allProducts.filter((p) => p.companyCode);
          if (productsWithCompany.length >= 1) {
            // 获取去重后的保险公司数量
            const uniqueCompanies = new Set(productsWithCompany.map((p) => p.companyCode));
            const isSameCompany = uniqueCompanies.size === 1;

            if (isSameCompany && !data.onlyProvideOneProductReason?.trim()) {
              errors.push({
                name: ["onlyProvideOneProductReason"],
                errors: ["請填寫只能提供一間保險公司建議之原因"],
              });
            }
          }

          if (errors.length > 0) {
            // 滚动到第一个错误字段
            if (errors[0].name) {
              if (errors[0].name[0] === "chooseProducts") {
                // 如果是产品选择错误，滚动到产品选择区域
                if (productSectionRef.current) {
                  productSectionRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              } else {
                // 如果是表单字段错误，使用表单的滚动方法
                form.scrollToField(errors[0].name);
              }
            }
            throw { errorFields: errors };
          }
        }

        return Promise.resolve();
      },
    }));

    useEffect(() => {
      // 同步财务资料基本信息
      form.setFieldsValue(data);

      // 同步 FNA 数据到表单字段
      if (activeSection === "fna") {
        form.setFieldsValue({
          // 三、財務需要分析
          insurancePurchaseObjectives: getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX"),
          additionalLifeCoverageAmountOption: getQuestionValue("financialAnalysis", "additionalLifeCoverageAmountOption", "RADIO"),
          additionalLifeCoverageAmountHKD: getQuestionValue("financialAnalysis", "additionalLifeCoverageAmountHKD", "INPUT"),
          additionalLifeCoverageAmountUSD: getQuestionValue("financialAnalysis", "additionalLifeCoverageAmountUSD", "INPUT"),
          additionalLifeCoverageAmount: getQuestionValue("financialAnalysis", "additionalLifeCoverageAmount", "INPUT"),
          additionalCriticalIllnessCoverageAmount: getQuestionValue("financialAnalysis", "additionalCriticalIllnessCoverageAmount", "INPUT"),
          additionalCriticalIllnessCoverageAmountOption: getQuestionValue("financialAnalysis", "additionalCriticalIllnessCoverageAmountOption", "RADIO"),
          additionalCriticalIllnessCoverageAmountHKD: getQuestionValue("financialAnalysis", "additionalCriticalIllnessCoverageAmountHKD", "INPUT"),
          additionalCriticalIllnessCoverageAmountUSD: getQuestionValue("financialAnalysis", "additionalCriticalIllnessCoverageAmountUSD", "INPUT"),
          insurancePurchaseMedicalObjectives: getQuestionValue("financialAnalysis", "insurancePurchaseMedicalObjectives", "RADIO"),
          additionalTargetAmountOption: getQuestionValue("financialAnalysis", "additionalTargetAmountOption", "RADIO"),
          additionalTargetAmountHKD: getQuestionValue("financialAnalysis", "additionalTargetAmountHKD", "INPUT"),
          additionalTargetAmountUSD: getQuestionValue("financialAnalysis", "additionalTargetAmountUSD", "INPUT"),
          additionalTargetYears: getQuestionValue("financialAnalysis", "additionalTargetYears", "INPUT"),
          insurancePurchaseOtherObjectiveOptions: getQuestionValue("financialAnalysis", "insurancePurchaseOtherObjectiveOptions", "RADIO"),
          additionalSavingsTargetReturnPeriod: getQuestionValue("financialAnalysis", "additionalSavingsTargetReturnPeriod", "INPUT"),
          additionalSavingsTargetReturnAmount: getQuestionValue("financialAnalysis", "additionalSavingsTargetReturnAmount", "INPUT"),
          additionalInvestmentTargetReturnPeriod: getQuestionValue("financialAnalysis", "additionalInvestmentTargetReturnPeriod", "INPUT"),
          additionalInvestmentTargetReturnAmount: getQuestionValue("financialAnalysis", "additionalInvestmentTargetReturnAmount", "INPUT"),
          insurancePurchaseOtherObjectives: getQuestionValue("financialAnalysis", "insurancePurchaseOtherObjectives", "INPUT"),
          investmentOptionManagementPreference: getQuestionValue("financialAnalysis", "investmentOptionManagementPreference", "RADIO"),
          investmentWithdrawalStartTime: getQuestionValue("financialAnalysis", "investmentWithdrawalStartTime", "RADIO"),
          investmentLongTermGoal: getQuestionValue("financialAnalysis", "investmentLongTermGoal", "RADIO"),
          investmentExpectationIfMarketPoor: getQuestionValue("financialAnalysis", "investmentExpectationIfMarketPoor", "RADIO"),
          policyTargetExpectedTime: getQuestionValue("financialAnalysis", "policyTargetExpectedTime", "RADIO"),
          premiumToDisposableIncomeRatio: getQuestionValue("financialAnalysis", "premiumToDisposableIncomeRatio", "RADIO"),
          premiumToLiquidAssetsRatio: getQuestionValue("financialAnalysis", "premiumToLiquidAssetsRatio", "RADIO"),
          intendedPremiumPaymentMethods: getQuestionValue("financialAnalysis", "intendedPremiumPaymentMethods", "CHECKBOX"),
          willingPremiumPaymentPeriodRange: getQuestionValue("financialAnalysis", "willingPremiumPaymentPeriodRange", "RADIO"),
          // 四、保費融資
          isPremiumFinanced: data.isPremiumFinanced,
          premiumFinancingRepaymentSource: data.premiumFinancingRepaymentSources?.[0],
          premiumFinancingRepaymentSourceOther: data.premiumFinancingRepaymentSourceOther,
          outstandingMortgageLoanAmount: data.outstandingMortgageLoanAmount,
          outstandingOtherLoanAmount: data.outstandingOtherLoanAmount,
          premiumFinancingEstimateConsent: data.premiumFinancingEstimateConsent === true ? "A" : data.premiumFinancingEstimateConsent === false ? "B" : undefined,
          premiumFinancingLenderName: data.premiumFinancing?.[0]?.lenderName,
          premiumFinancingLoanAmount: data.premiumFinancing?.[0]?.loanAmount,
          premiumFinancingLoanInterestRate: data.premiumFinancing?.[0]?.loanInterestRate,
          premiumFinancingLoanTerm: data.premiumFinancing?.[0]?.loanTerm,
          premiumFinancingRepaymentAmount: data.premiumFinancing?.[0]?.repaymentAmount,
          premiumFinancingImportantDisclosureConfirmed: data.premiumFinancingImportantDisclosureConfirmed,
          premiumFinancingRepaymentConfirmed: data.premiumFinancingRepaymentConfirmed,
          ...(!isZuuFinancialStepMode
            ? {
                hasExistingPolicyAsMortgage: data.hasExistingPolicyAsMortgage,
                usingExistingPolicyFundsForNewPolicy: getQuestionValue("transferInsuranceDeclaration", "usingExistingPolicyFundsForNewPolicy", "RADIO"),
                intermediaryAssessmentAndComments: getQuestionValue("transferInsuranceDeclaration", "intermediaryAssessmentAndComments", "RADIO"),
                intermediaryAssessmentAndCommentsOther: getQuestionValue("transferInsuranceDeclaration", "intermediaryAssessmentAndCommentsOther", "INPUT"),
              }
            : {}),
          // 七、建議及原因
          suitabilityDeviationOccurred: getQuestionValue("suggestionsAndReasons", "suitabilityDeviationOccurred", "RADIO"),
          suitabilityDeviationFollowUpYesOptions: getQuestionValue("suggestionsAndReasons", "suitabilityDeviationFollowUpYesOptions", "RADIO"),
          suitabilityDeviationFollowUpNoOptions: getQuestionValue("suggestionsAndReasons", "suitabilityDeviationFollowUpNoOptions", "RADIO"),
          reasonForRecommendingDespiteDeviation: getQuestionValue("suggestionsAndReasons", "reasonForRecommendingDespiteDeviation", "RADIO"),
          otherReasonForRecommendation: getQuestionValue("suggestionsAndReasons", "otherReasonForRecommendation", "INPUT"),
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, form, activeSection]);

    // 调用保费承担能力计算 API
    useEffect(() => {
      const fetchPremiumCapacity = async () => {
        if (isZuuFinancialStepMode) {
          setPremiumCapacity(null);
          return;
        }

        if (!orderId || !data.financialAnalysis || data.financialAnalysis.length === 0) {
          return;
        }

        try {
          const response = await calculatePremiumCapacity({
            orderId: Number(orderId),
            financialAnalysis: data.financialAnalysis,
          });
          setPremiumCapacity(response);
        } catch (error) {
          console.error("获取保费承担能力失败:", error);
          // API 错误会由全局错误处理器显示，这里只需要记录日志
        }
      };

      fetchPremiumCapacity();
    }, [orderId, data.financialAnalysis, isZuuFinancialStepMode]);

    const [companyList, setCompanyList] = useState<ProductCompanyListVO[]>([]);
    const newChooseProduct: ChooseProduct = {
      productCode: null,
      choose: false,
    };
    const { selectMetadataList } = useMetadataStore();

    const getOptions = (code: string, fallback: any[] = []) => {
      const meta = selectMetadataList.find((m) => m.code === code);
      if (meta?.items?.length) {
        return meta.items.map((i) => ({
          label: i.name || i.key,
          value: i.key,
        }));
      }
      return fallback;
    };

    useEffect(() => {
      if (orderId) {
        getProductCompanyList().then((res) => {
          setCompanyList(res);
        });
        // getProductList({ pageSize: 1000 }).then((res) => {
        //   setProductList(res.content || []);
        // });
      }
    }, [orderId]);

    // Validate products against payment term and benefit period requirements
    useEffect(() => {
      const validateProducts = async () => {
        const products = data.chooseProducts || [];
        const validation: Record<number, { paymentTermValid: boolean; benefitTermValid: boolean }> = {};

        // Get derived requirements from FNA
        const q4_1 = getQuestionValue("financialAnalysis", "willingPremiumPaymentPeriodRange", "RADIO");
        const q2 = getQuestionValue("financialAnalysis", "policyTargetExpectedTime", "RADIO");

        for (let i = 0; i < products.length; i++) {
          const product = products[i];
          if (!product.productCode) {
            validation[i] = { paymentTermValid: true, benefitTermValid: true };
            continue;
          }

          try {
            const productInfo: ProductInfoVO = await getProductInfo(product.productCode);

            // Validate payment term (供款期数)
            let paymentTermValid = true;
            if (q4_1 && productInfo.paymentTerm) {
              const paymentTermMap: Record<string, string[]> = {
                A: ["2", "3", "4", "5"],
                B: ["6", "7", "8", "9", "10"],
                C: ["11", "12", "13", "14", "15"],
                D: ["16", "17", "18", "19", "20"],
                E: ["21", "22", "23", "24", "25", "30", "35", "40", "45", "50"],
                F: ["whole_life", "to_age_100"],
                G: ["1"], // 一次性付款
              };

              const expectedTerms = paymentTermMap[q4_1];
              if (expectedTerms) {
                paymentTermValid = productInfo.paymentTerm.some((term) => expectedTerms.includes(term));
              }
            }

            // Validate benefit period (保障年限)
            let benefitTermValid = true;
            if (q2 && productInfo.policyPeriods) {
              if (q2 === "G") {
                benefitTermValid = productInfo.policyPeriods.includes("whole_life");
              } else {
                // For other options, accept to_age_100 or numeric periods
                benefitTermValid = productInfo.policyPeriods.some((period) => period === "to_age_100" || !isNaN(Number(period)));
              }
            }

            validation[i] = { paymentTermValid, benefitTermValid };
          } catch (error) {
            console.error(`Failed to validate product ${product.productCode}:`, error);
            validation[i] = { paymentTermValid: true, benefitTermValid: true };
          }
        }

        setProductValidation(validation);
      };

      if (data.chooseProducts && data.chooseProducts.length > 0) {
        validateProducts();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.chooseProducts, data.financialAnalysis]);

    // Auto-update onlyProvideOneProduct based on all products' company codes
    useEffect(() => {
      // 需求 1）：判断所有填写了产品计划的卡片是否来自同一家保险公司
      const allProducts = data.chooseProducts || [];
      const productsWithCompany = allProducts.filter((p) => p.companyCode);

      // 获取去重后的保险公司数量
      const uniqueCompanies = new Set(productsWithCompany.map((p) => p.companyCode));
      const isSameCompany = productsWithCompany.length > 0 && uniqueCompanies.size === 1;

      // 只有当计算结果与当前值不同时才更新，避免无限循环
      if (data.onlyProvideOneProduct !== isSameCompany) {
        onChange({ ...data, onlyProvideOneProduct: isSameCompany });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.chooseProducts, data.onlyProvideOneProduct]);

    // Initialize chooseProducts with 1 empty product if empty
    useEffect(() => {
      // 只在 FNA 页面且产品列表为空时初始化
      if (activeSection === "fna" && (!data.chooseProducts || data.chooseProducts.length === 0)) {
        const emptyProduct: ChooseProduct = {
          productCode: null,
          choose: false,
        };
        onChange({
          ...data,
          chooseProducts: [{ ...emptyProduct }],
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    // Initialize numeric fields with 0 when financial info is required
    useEffect(() => {
      if (!isZuuFinancialStepMode && isFinancialInfoRequired && activeSection === "financial-info") {
        // Helper to merge defaults with existing values, converting null to 0 for numbers
        const mergeWithDefaults = (current: any, defaults: any) => {
          if (!current) return defaults;
          const merged = { ...current, ...defaults };
          Object.keys(defaults).forEach((key) => {
            if (current[key] !== undefined && current[key] !== null) {
              merged[key] = current[key];
            }
          });
          return merged;
        };

        let initializedData = {
          ...data,
          income: mergeWithDefaults(data.income, {
            salaryCommissionBonus: 0,
            rentalIncome: 0,
            hasOtherIncome: false,
            otherIncomeName: "",
            otherIncomeAmount: 0,
          }),
          expense: mergeWithDefaults(data.expense, {
            personalAndFamilyExpense: 0,
            mortgageLoanRepayment: 0,
            insuranceContribution: 0,
            hasOtherExpense: false,
            otherExpenseName: "",
            otherExpenseAmount: 0,
          }),
          currentAssets: mergeWithDefaults(data.currentAssets, {
            cashAndDeposits: 0,
            liquidInvestments: 0,
            hasOtherCurrentAssets: false,
            otherCurrentAssetsName: "",
            otherCurrentAssetsAmount: 0,
          }),
          nonCurrentAssets: mergeWithDefaults(data.nonCurrentAssets, {
            property: 0,
            otherNonCurrentAssets: 0,
          }),
          liabilities: mergeWithDefaults(data.liabilities, {
            mortgageLoan: 0,
            personalCreditCardLoan: 0,
            hasOtherLiabilities: false,
            otherLiabilityName: "",
            otherLiabilityAmount: 0,
          }),
          protection: mergeWithDefaults(data.protection, {
            lifeInsurance: 0,
            criticalIllnessInsurance: 0,
            medicalInsuranceType: undefined,
            medicalInsuranceAmount: 0,
            accidentInsurance: 0,
            otherExistingProtection: 0,
            attachmentRemarks: "",
          }),
        };

        // 应用 formRequiredConfig 中的默认值
        const dataWithConfigDefaults = applyDefaultValues(initializedData);
        if (dataWithConfigDefaults !== initializedData) {
          initializedData = dataWithConfigDefaults;
        }

        // Only update if there are actual changes
        const hasChanges =
          JSON.stringify(initializedData.income) !== JSON.stringify(data.income) ||
          JSON.stringify(initializedData.expense) !== JSON.stringify(data.expense) ||
          JSON.stringify(initializedData.currentAssets) !== JSON.stringify(data.currentAssets) ||
          JSON.stringify(initializedData.nonCurrentAssets) !== JSON.stringify(data.nonCurrentAssets) ||
          JSON.stringify(initializedData.liabilities) !== JSON.stringify(data.liabilities) ||
          JSON.stringify(initializedData.protection) !== JSON.stringify(data.protection);

        if (hasChanges) {
          onChange(initializedData);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFinancialInfoRequired, activeSection, data, onChange, isZuuFinancialStepMode]);

    // ZUU 没有 4.2，确保遗留值被清空
    useEffect(() => {
      if (activeSection !== "fna" || !isZuuFinancialStepMode) return;

      const currentFinancialAnalysis = (data.financialAnalysis || []).filter((q) => q.question);
      const nextFinancialAnalysis = currentFinancialAnalysis.filter((q) => q.question !== "continuedContributionReason" && q.question !== "continuedContributionReasonOther");

      if (nextFinancialAnalysis.length !== currentFinancialAnalysis.length) {
        form.setFieldValue("continuedContributionReason", undefined);
        form.setFieldValue("continuedContributionReasonOther", undefined);
        onChange({ ...data, financialAnalysis: nextFinancialAnalysis });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.financialAnalysis, activeSection, isZuuFinancialStepMode]);

    // 需求1：当问题1的选项E取消勾选时，清空投资相关字段
    useEffect(() => {
      if (activeSection !== "fna") return;

      const objectives = getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX");

      // 如果选项E未被勾选，清空所有投资相关字段
      if (!objectives || !objectives.includes("E")) {
        const currentFinancialAnalysis = (data.financialAnalysis || []).filter((q) => q.question);
        const keysToCheck = ["investmentOptionManagementPreference", "investmentWithdrawalStartTime", "investmentLongTermGoal", "investmentExpectationIfMarketPoor"];

        let hasValue = false;
        keysToCheck.forEach((key) => {
          const q = currentFinancialAnalysis.find((q) => q.question === key);
          if (q) {
            const hasNonEmpty = q.items?.some((i) => i.value !== false && i.value !== "" && i.value !== 0 && i.value !== undefined && i.value !== null);
            if (hasNonEmpty) hasValue = true;
          }
        });

        if (hasValue) {
          const newList = currentFinancialAnalysis.map((q) => {
            if (keysToCheck.includes(q.question || "")) {
              // 清空该问题的所有值
              if (q.type === "RADIO" || q.type === "CHECKBOX") {
                return {
                  ...q,
                  items: (q.items || []).map((i) => ({ ...i, value: false })),
                };
              } else if (q.type === "INPUT") {
                return {
                  ...q,
                  items: [{ key: "A", value: "" }],
                };
              }
            }
            return q;
          });

          // 清空表单验证状态
          keysToCheck.forEach((key) => {
            form.setFieldValue(key, undefined);
          });

          onChange({ ...data, financialAnalysis: newList });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.financialAnalysis, activeSection]);

    // 需求2：当 investmentOptionManagementPreference 从 A 切换到 B 或清空时，清空子问题
    useEffect(() => {
      if (activeSection !== "fna") return;

      const managementPreference = getQuestionValue("financialAnalysis", "investmentOptionManagementPreference", "RADIO");

      // 如果值不是 "A"，清空子问题
      if (managementPreference !== "A") {
        const currentFinancialAnalysis = (data.financialAnalysis || []).filter((q) => q.question);
        const keysToCheck = ["investmentWithdrawalStartTime", "investmentLongTermGoal", "investmentExpectationIfMarketPoor"];

        let hasValue = false;
        keysToCheck.forEach((key) => {
          const q = currentFinancialAnalysis.find((q) => q.question === key);
          if (q) {
            const hasNonEmpty = q.items?.some((i) => i.value !== false && i.value !== "" && i.value !== 0 && i.value !== undefined && i.value !== null);
            if (hasNonEmpty) hasValue = true;
          }
        });

        if (hasValue) {
          const newList = currentFinancialAnalysis.map((q) => {
            if (keysToCheck.includes(q.question || "")) {
              // 清空该问题的所有值
              if (q.type === "RADIO" || q.type === "CHECKBOX") {
                return {
                  ...q,
                  items: (q.items || []).map((i) => ({ ...i, value: false })),
                };
              } else if (q.type === "INPUT") {
                return {
                  ...q,
                  items: [{ key: "A", value: "" }],
                };
              }
            }
            return q;
          });

          // 清空表单验证状态
          keysToCheck.forEach((key) => {
            form.setFieldValue(key, undefined);
          });

          onChange({ ...data, financialAnalysis: newList });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.financialAnalysis, activeSection]);

    const handleChange = (field: keyof OrderFinancialVO | string, value: any) => {
      onChange({ ...data, [field]: value });
    };

    const handleNestedChange = (section: keyof OrderFinancialVO, field: string, value: any) => {
      const currentSection = (data[section] as any) || {};
      const newData = {
        ...data,
        [section]: { ...currentSection, [field]: value },
      };
      onChange(newData);
      // Form values are synced via useEffect, but for immediate feedback we might want to set them?
      // Actually the useEffect [data] will handle it, but it might be slightly delayed.
      // Better to set form value directly too to avoid lag if controlled.
      form.setFieldValue([section, field], value);
    };

    // Premium Financing Helpers
    const handleAddPremiumFinancing = () => {
      const currentList = data.premiumFinancing || [];
      onChange({
        ...data,
        premiumFinancing: [...currentList, {}],
      });
    };

    const handleRemovePremiumFinancing = (index: number) => {
      const currentList = data.premiumFinancing || [];
      const newList = currentList.filter((_, i) => i !== index);
      onChange({
        ...data,
        premiumFinancing: newList,
      });
    };

    const handlePremiumFinancingChange = (index: number, field: string, value: any) => {
      const currentList = data.premiumFinancing || [];
      const newList = [...currentList];
      newList[index] = { ...newList[index], [field]: value };
      onChange({
        ...data,
        premiumFinancing: newList,
      });
    };

    // New helpers for Question-based structure (matching fna.json)
    const updateQuestionValue = (
      section: "financialAnalysis" | "transferInsuranceDeclaration" | "suggestionsAndReasons",
      questionKey: string,
      value: any,
      type: "RADIO" | "CHECKBOX" | "INPUT" = "RADIO",
    ) => {
      // Filter out invalid entries (e.g. items without question key) to ensure data structure consistency
      const list = (data[section] || []).filter((q) => q.question);
      const newList = [...list];
      const { canonicalKey, storedType, storedValue } = toZuuFnaStoredQuestionValue(questionKey, value, type);
      const aliases = getZuuFnaQuestionAliases(questionKey);
      const questionIndex = newList.findIndex((q) => aliases.includes(q.question));

      let newItems: any[] = [];
      if (questionIndex >= 0) {
        newItems = [...(newList[questionIndex].items || [])];
      }

      if (storedType === "INPUT") {
        // Update item with key 'A'
        const itemIndex = newItems.findIndex((i) => i.key === "A");
        if (itemIndex >= 0) {
          newItems[itemIndex] = { ...newItems[itemIndex], value: storedValue };
        } else {
          newItems.push({ key: "A", value: storedValue });
        }
      } else if (storedType === "RADIO") {
        // For RADIO, ensure we have items for the selected value
        // If we don't have all options in newItems, we might need to assume them?
        // But we can just set value=true for the selected key, and false for others we know about.
        // Ideally we should know all possible keys to set them to false.
        // But here we iterate existing items and update.
        // If the selected key is NOT in existing items, we add it.

        const key = storedValue;
        let keyFound = false;
        newItems = newItems.map((i) => {
          if (i.key === key) {
            keyFound = true;
            return { ...i, value: true };
          }
          return { ...i, value: false };
        });

        if (!keyFound && key) {
          newItems.push({ key, value: true });
        }
      } else if (storedType === "CHECKBOX") {
        const keys = (storedValue as string[]) || [];

        // Update existing items
        newItems = newItems.map((i) => {
          const isSelected = keys.includes(i.key);
          return { ...i, value: isSelected };
        });

        // Add new selected items that don't exist yet
        keys.forEach((k) => {
          if (!newItems.find((i) => i.key === k)) {
            newItems.push({ key: k, value: true });
          }
        });
      }

      if (questionIndex >= 0) {
        newList[questionIndex] = { ...newList[questionIndex], question: canonicalKey, items: newItems, type: storedType === "CHECKBOX" ? "CHECKBOX" : storedType === "INPUT" ? "INPUT" : "RADIO" };
      } else {
        // Create new question
        newList.push({
          question: canonicalKey,
          type: storedType === "CHECKBOX" ? "CHECKBOX" : storedType === "INPUT" ? "INPUT" : "RADIO",
          items: newItems,
        });
        // Note: The 'type' in FormData is typically "RADIO" for choice questions even if multiple choice (checkbox).
        // Or maybe "CHECKBOX"? order.ts doesn't specify Enum for type strictly here, it says Type.
        // Assuming "RADIO" is safe for now or "CHECKBOX" if backend supports it.
        // Let's use the passed type but map CHECKBOX to CHECKBOX if that's what we want.
        // Actually, looking at transferInsuranceDeclaration usage, it sets type="RADIO" even for choices.
        // Let's stick to passing the type.
      }

      if (section === "financialAnalysis" && ["additionalLifeCoverageAmountOption", "additionalCriticalIllnessCoverageAmountOption", "additionalTargetAmountOption"].includes(canonicalKey)) {
        console.log("[ZUU][updateQuestionValue]", {
          questionKey: canonicalKey,
          value: storedValue,
          type: storedType,
          newItems,
          questionAfterUpdate: newList.find((q) => q.question === canonicalKey),
        });
      }

      onChange({ ...data, [section]: newList });
    };

    // --- Subtotal Calculations ---
    const parsePositiveInteger = (value: unknown): number | undefined => {
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return undefined;
      }
      const normalized = Math.trunc(value);
      return normalized >= 0 ? normalized : undefined;
    };

    // A) Income
    const totalIncomeDerived =
      (data.income?.salaryCommissionBonus || 0) + (data.income?.rentalIncome || 0) + (data.income?.hasOtherIncome && data.income?.otherIncomeAmount ? data.income.otherIncomeAmount : 0);
    const totalIncome = isZuuFinancialStepMode ? (parsePositiveInteger((data as any).monthlyIncomesTotal) ?? totalIncomeDerived) : totalIncomeDerived;

    // B) Expense
    const totalExpenseDerived =
      (data.expense?.personalAndFamilyExpense || 0) +
      (data.expense?.mortgageLoanRepayment || 0) +
      (data.expense?.insuranceContribution || 0) +
      (data.expense?.hasOtherExpense && data.expense?.otherExpenseAmount ? data.expense.otherExpenseAmount : 0);
    const totalExpense = isZuuFinancialStepMode ? (parsePositiveInteger((data as any).monthlyExpensesTotal) ?? totalExpenseDerived) : totalExpenseDerived;

    // C) Current Assets
    const totalCurrentAssetsDerived =
      (data.currentAssets?.cashAndDeposits || 0) +
      (data.currentAssets?.liquidInvestments || 0) +
      (data.currentAssets?.hasOtherCurrentAssets && data.currentAssets?.otherCurrentAssetsAmount ? data.currentAssets.otherCurrentAssetsAmount : 0);
    const totalCurrentAssets = isZuuFinancialStepMode ? (parsePositiveInteger((data as any).liquidAssetsTotal) ?? totalCurrentAssetsDerived) : totalCurrentAssetsDerived;

    // D) Non-Current Assets
    const totalNonCurrentAssets = (data.nonCurrentAssets?.property || 0) + (data.nonCurrentAssets?.otherNonCurrentAssets || 0);

    // E) Liabilities
    const totalLiabilities =
      (data.liabilities?.mortgageLoan || 0) +
      (data.liabilities?.personalCreditCardLoan || 0) +
      (data.liabilities?.hasOtherLiabilities && data.liabilities?.otherLiabilityAmount ? data.liabilities.otherLiabilityAmount : 0);

    // Main Summaries
    const surplusDeficit = totalIncome - totalExpense;
    const netAssets = totalCurrentAssets + totalNonCurrentAssets - totalLiabilities;

    // Header Box Style
    const headerBoxStyle: React.CSSProperties = {
      backgroundColor: isZuuFinancialStepMode ? "#E9EEF5" : "var(--theme-tertiary)",
      border: isZuuFinancialStepMode ? "2px solid #3E679A" : "1px solid var(--theme-primary)",
      borderRadius: isZuuFinancialStepMode ? 8 : 4,
      padding: isZuuFinancialStepMode ? "12px 16px" : "10px 16px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 24,
      color: isZuuFinancialStepMode ? "#274C77" : "var(--theme-primary)",
      fontSize: isZuuFinancialStepMode ? 15 : 16,
      fontWeight: 600,
    };

    // Subsection Header Style
    const subSectionHeaderStyle: React.CSSProperties = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: isZuuFinancialStepMode ? 12 : 16,
      color: isZuuFinancialStepMode ? "#475569" : "var(--theme-primary)",
      fontSize: 14,
    };

    const insetPanelStyle: React.CSSProperties = {
      backgroundColor: "#F5F5F7",
      padding: 16,
      borderRadius: 6,
      marginTop: 12,
    };

    const neutralInsetPanelStyle: React.CSSProperties = {
      background: "#F5F5F5",
      padding: 16,
      borderRadius: 6,
    };

    // Calculate values for section 3 display
    const getRatioFromKey = (key: string | undefined): number => {
      switch (key) {
        case "A":
          return 0.1;
        case "B":
          return 0.2;
        case "C":
          return 0.3;
        case "D":
          return 0.4;
        case "E":
          return 0.5;
        case "F":
          return 1.0;
        default:
          return 0;
      }
    };

    const ratioA = getRatioFromKey(getQuestionValue("financialAnalysis", "premiumToDisposableIncomeRatio", "RADIO"));
    const ratioB = getRatioFromKey(getQuestionValue("financialAnalysis", "premiumToLiquidAssetsRatio", "RADIO"));

    // 使用 API 返回的数据，如果没有则使用本地计算作为后备
    const a_monthly = premiumCapacity?.monthlyDisposableIncome ?? surplusDeficit;
    const a_yearly = premiumCapacity?.annualDisposableIncome ?? surplusDeficit * 12;
    const a1_monthly = premiumCapacity?.maxMonthlyPremium ?? a_monthly * ratioA;
    const a1_yearly = premiumCapacity?.maxAnnualPremium ?? a_yearly * ratioA;

    const b_assets = premiumCapacity?.liquidAssets ?? totalCurrentAssets;
    const b1_amount = premiumCapacity?.maxLiquidAssetPremium ?? b_assets * ratioB;
    const annualizePremium = (premiumPerPeriod?: number, paymentFrequency?: string) => {
      if (premiumPerPeriod === undefined || premiumPerPeriod === null || !Number.isFinite(Number(premiumPerPeriod))) {
        return 0;
      }

      const multiplierMap: Record<string, number> = {
        SINGLE_PAYMENT: 1,
        ANNUALLY: 1,
        SEMI_ANNUALLY: 2,
        QUARTERLY: 4,
        MONTHLY: 12,
      };

      return Number(premiumPerPeriod) * (multiplierMap[paymentFrequency || ""] || 1);
    };

    const totalRecommendedAnnualPremium = (orderData?.productPlans || []).reduce((sum, plan) => {
      const basePremium = annualizePremium(plan?.premiumPerPeriod, plan?.paymentFrequency);
      const riderPremium = (plan?.riders || []).reduce((riderSum: number, rider: any) => riderSum + annualizePremium(rider?.premiumPerPeriod, rider?.paymentFrequency), 0);
      return sum + basePremium + riderPremium;
    }, 0);

    const zuuStep2Draft = deriveZuuStep2Draft({
      financial: data,
      activeSection,
      isFinancialInfoRequired,
    });

    const updateZuuDraftAmount = (
      group: "availableIncome" | "liquidAssets",
      field: "monthlyIncomeLast24Months" | "monthlyExpenseLast24Months" | "currentLiquidAssets" | "monthlyDisposableIncomeAfterRetirement" | "additionalLiquidAssetsAfterRetirement",
      value: number | undefined,
    ) => {
      const normalizedValue = parsePositiveInteger(value);
      const nextData: OrderFinancialVO = {
        ...data,
        monthlyIncomesTotal: undefined,
        monthlyExpensesTotal: undefined,
        liquidAssetsTotal: undefined,
        income: {
          ...(data.income || {}),
        },
        expense: {
          ...(data.expense || {}),
        },
        currentAssets: {
          ...(data.currentAssets || {}),
        },
      };

      if (field === "monthlyIncomeLast24Months") {
        nextData.income = {
          ...(nextData.income || {}),
          avgMonthlyIncome: normalizedValue ?? null,
        };
      } else if (field === "monthlyExpenseLast24Months") {
        nextData.expense = {
          ...(nextData.expense || {}),
          avgMonthlyExpense: normalizedValue ?? null,
        };
      } else if (field === "currentLiquidAssets") {
        nextData.currentAssets = {
          ...(nextData.currentAssets || {}),
          currentLiquidAssets: normalizedValue ?? null,
        };
      } else if (field === "monthlyDisposableIncomeAfterRetirement") {
        nextData.income = {
          ...(nextData.income || {}),
          expectedRetireIncome: normalizedValue ?? null,
        };
      } else if (field === "additionalLiquidAssetsAfterRetirement") {
        nextData.currentAssets = {
          ...(nextData.currentAssets || {}),
          expectedRetireLiquidAssets: normalizedValue ?? null,
        };
      }

      onChange(nextData);
    };

    const updateZuuDraftFreeAmount = (
      group: "availableIncome" | "liquidAssets",
      field: "monthlyDisposableIncomeAfterRetirement" | "additionalLiquidAssetsAfterRetirement",
      value: number | undefined,
    ) => {
      const normalizedValue = parsePositiveInteger(value);
      const nextData: OrderFinancialVO = {
        ...data,
        income: {
          ...(data.income || {}),
        },
        currentAssets: {
          ...(data.currentAssets || {}),
        },
      };

      if (field === "monthlyDisposableIncomeAfterRetirement") {
        nextData.income = {
          ...(nextData.income || {}),
          expectedRetireIncome: normalizedValue ?? null,
        };
      } else if (field === "additionalLiquidAssetsAfterRetirement") {
        nextData.currentAssets = {
          ...(nextData.currentAssets || {}),
          expectedRetireLiquidAssets: normalizedValue ?? null,
        };
      }

      onChange(nextData);
    };

    const formatCurrency = (val: number) => {
      return val.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const formatInteger = (val: number) => {
      return val.toLocaleString("en-US", { maximumFractionDigits: 0 });
    };
    const monthlyCurrencySuffix = isZuuFinancialStepMode ? "HKD/月" : undefined;
    const currencySuffix = isZuuFinancialStepMode ? "HKD" : undefined;
    const currencySuffixVariant = isZuuFinancialStepMode ? "box" : "plain";
    const showZuuRetirementFields = shouldShowZuuRetirementFields(orderData?.policyholder);
    const intendedPremiumPaymentMethods = (getQuestionValue("financialAnalysis", "intendedPremiumPaymentMethods", "CHECKBOX") || []) as string[];
    const premiumCapacityWarnings: string[] = [];
    if (isZuuFinancialStepMode && totalRecommendedAnnualPremium > 0) {
      if (intendedPremiumPaymentMethods.includes("A") && a1_yearly > 0 && totalRecommendedAnnualPremium > a1_yearly) {
        premiumCapacityWarnings.push(`按每月可動用收入推算，現有產品計劃的預估年繳保費 ${formatCurrency(totalRecommendedAnnualPremium)} HKD 高於可負擔上限 ${formatCurrency(a1_yearly)} HKD。`);
      }
      if (intendedPremiumPaymentMethods.includes("B") && b1_amount > 0 && totalRecommendedAnnualPremium > b1_amount) {
        premiumCapacityWarnings.push(`按流動資產推算，現有產品計劃的預估年繳保費 ${formatCurrency(totalRecommendedAnnualPremium)} HKD 高於可負擔上限 ${formatCurrency(b1_amount)} HKD。`);
      }
    }
    const zuuFinancialAnalysisProps: ZuuFinancialAnalysisSectionProps | undefined = isZuuFinancialStepMode
      ? {
          data,
          onChange,
          getQuestionValue,
          updateQuestionValue,
          shouldShowQuestion42,
          metrics: {
            aMonthly: a_monthly,
            aYearly: a_yearly,
            a1Monthly: a1_monthly,
            a1Yearly: a1_yearly,
            bAssets: b_assets,
            b1Amount: b1_amount,
          },
        }
      : undefined;
    const zuuPremiumFinancingProps: ZuuPremiumFinancingSectionProps | undefined = isZuuFinancialStepMode
      ? {
          data,
          onChange,
          readOnly,
          companyOptions: companyList.map((company) => ({
            label: company.name || company.code,
            value: company.code,
          })),
          onFieldChange: handleChange,
          onAddPremiumFinancing: handleAddPremiumFinancing,
          onRemovePremiumFinancing: handleRemovePremiumFinancing,
          onPremiumFinancingChange: handlePremiumFinancingChange,
        }
      : undefined;
    const zuuProductSelectionProps: ZuuProductSelectionSectionProps | undefined = isZuuFinancialStepMode
      ? {
          data,
          onChange,
          readOnly,
          productSectionRef,
          productValidation,
          getQuestionValue,
          onOpenSelector,
          onMessageError: message.error,
        }
      : undefined;
    const zuuOnlyProvideOneProductProps: ZuuOnlyProvideOneProductSectionProps | undefined = isZuuFinancialStepMode
      ? {
          data,
          onChange,
        }
      : undefined;
    const zuuSuggestionsProps: ZuuSuggestionsSectionProps | undefined = isZuuFinancialStepMode
      ? {
          data,
          onChange,
          getQuestionValue,
          updateQuestionValue,
        }
      : undefined;

    return (
      <div className="financial-form" style={{ borderRadius: 6 }}>
        {/* 只有开关打开时才显示下面两个部分 */}
        {isFinancialInfoRequired && (
          <>
            {activeSection === "financial-info" && isZuuFinancialStepMode && (
              <Form form={form} layout="vertical" disabled={readOnly} className={cx(styles.financialForm, isZuuFinancialStepMode && styles.zuuFinancialStepForm)}>
                <ZuuFinancialInfoStep2Section
                  data={data}
                  onChange={onChange}
                  activeSection={activeSection}
                  isFinancialInfoRequired={isFinancialInfoRequired}
                  readOnly={readOnly}
                  showRetirementFields={showZuuRetirementFields}
                  detailDisplay={detailDisplay}
                />
              </Form>
            )}
            {activeSection === "financial-info" && !isZuuFinancialStepMode && (
              <Form form={form} layout="vertical" disabled={readOnly} className={cx(styles.financialForm, isZuuFinancialStepMode && styles.zuuFinancialStepForm)}>
                <>
                  {/* 1. 估計每月盈餘/超支 */}
                  <CustomCard title="一、財務資料" className={cx(styles.sectionCard, isZuuFinancialStepMode && styles.zuuSectionCard)}>
                    <div style={headerBoxStyle}>
                      <span>{isZuuFinancialStepMode ? "1. 可動用收入" : "1. 估計每月盈餘/超支 (HK$/港幣)"}</span>
                      <span
                        style={
                          isZuuFinancialStepMode
                            ? {
                                fontSize: 11,
                                color: "#5b6f87",
                                fontWeight: 500,
                              }
                            : undefined
                        }
                      >
                        {isZuuFinancialStepMode ? "＊每月可動用收入將等同每月收入減去每月開支" : `A-B = ${surplusDeficit.toLocaleString()} HKD`}
                      </span>
                    </div>

                    {/* Income (A) */}
                    <div style={{ marginBottom: 24, borderRadius: 6 }}>
                      <div style={subSectionHeaderStyle}>
                        <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                          {isZuuFinancialStepMode ? "1a. 在過去二十四個月裡，您從所有收入來源所得的每月平均收入為？" : "A) 過去2年的平均每月收入"}
                        </Title>
                        {!isZuuFinancialStepMode && <span style={{ color: "#333", fontWeight: 500 }}>小計:{totalIncome.toLocaleString()} HKD</span>}
                      </div>
                      {isZuuFinancialStepMode && <div className={styles.zuuQuestionNote}>包括薪金、花紅、佣金、其他薪酬福利、物業租賃收入、銀行存款利息、債券利息及股息等。</div>}
                      {isZuuFinancialStepMode && (
                        <>
                          <div className={styles.zuuSummaryField}>
                            <AmountInput
                              style={{ width: "100%" }}
                              suffix={monthlyCurrencySuffix}
                              suffixVariant={currencySuffixVariant}
                              value={zuuStep2Draft.financialInfo.availableIncome.monthlyIncomeLast24Months.amount}
                              placeholder="請輸入"
                              onChange={(val) => updateZuuDraftAmount("availableIncome", "monthlyIncomeLast24Months", val)}
                            />
                          </div>
                        </>
                      )}
                      {!isZuuFinancialStepMode && (
                        <Row gutter={24}>
                          <Col span={12}>
                            <Form.Item label="薪金/佣金/花紅" name={["income", "salaryCommissionBonus"]}>
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={monthlyCurrencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.income?.salaryCommissionBonus ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("income", "salaryCommissionBonus", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="租金收入">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={monthlyCurrencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.income?.rentalIncome ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("income", "rentalIncome", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 8,
                              }}
                            >
                              <Text>是否有其他收入 ?</Text>
                              <Switch
                                checkedChildren="是"
                                unCheckedChildren="否"
                                checked={data.income?.hasOtherIncome || false}
                                onChange={(val) => {
                                  if (!val) {
                                    const newData = {
                                      ...data,
                                      income: {
                                        ...data.income,
                                        hasOtherIncome: false,
                                        otherIncomeName: undefined,
                                        otherIncomeAmount: undefined,
                                      },
                                    };
                                    onChange(newData);
                                    form.setFieldValue("income", newData.income);
                                  } else {
                                    handleNestedChange("income", "hasOtherIncome", val);
                                  }
                                }}
                              />
                            </div>
                            {data.income?.hasOtherIncome && (
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item label="名稱">
                                    <Input placeholder="請輸入" value={data.income?.otherIncomeName} onChange={(e) => handleNestedChange("income", "otherIncomeName", e.target.value)} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="金額">
                                    <AmountInput
                                      style={{ width: "100%" }}
                                      suffix={monthlyCurrencySuffix}
                                      suffixVariant={currencySuffixVariant}
                                      placeholder="請輸入"
                                      value={data.income?.otherIncomeAmount ?? 0}
                                      onChange={(val) => {
                                        handleNestedChange("income", "otherIncomeAmount", val ?? 0);
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      )}

                      {/* Expense (B) */}
                      <Divider style={{ margin: "24px 0" }} />
                      <div style={subSectionHeaderStyle}>
                        <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                          {isZuuFinancialStepMode ? "1b. 在過去二十四個月裡，您每月平均開支為？" : "B) 過去2年的平均每月支出"}
                        </Title>
                        {!isZuuFinancialStepMode && <span style={{ color: "#333", fontWeight: 500 }}>小計:{totalExpense.toLocaleString()} HKD</span>}
                      </div>
                      {isZuuFinancialStepMode && <div className={styles.zuuQuestionNote}>包括樓宇按揭、租金、衣服、交通、借貸、保險費用及保費融資產生的利息等。</div>}
                      {isZuuFinancialStepMode && (
                        <>
                          <div className={styles.zuuSummaryField}>
                            <AmountInput
                              style={{ width: "100%" }}
                              suffix={monthlyCurrencySuffix}
                              suffixVariant={currencySuffixVariant}
                              value={zuuStep2Draft.financialInfo.availableIncome.monthlyExpenseLast24Months.amount}
                              placeholder="請輸入"
                              onChange={(val) => updateZuuDraftAmount("availableIncome", "monthlyExpenseLast24Months", val)}
                            />
                          </div>
                        </>
                      )}
                      {!isZuuFinancialStepMode && (
                        <Row gutter={24}>
                          <Col span={12}>
                            <Form.Item label="個人及家庭開支">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={monthlyCurrencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.expense?.personalAndFamilyExpense ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("expense", "personalAndFamilyExpense", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="按揭/借貸還款">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={monthlyCurrencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.expense?.mortgageLoanRepayment ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("expense", "mortgageLoanRepayment", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="保險供款">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={monthlyCurrencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.expense?.insuranceContribution ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("expense", "insuranceContribution", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 8,
                              }}
                            >
                              <Text>是否有其他支出 ?</Text>
                              <Switch
                                checkedChildren="是"
                                unCheckedChildren="否"
                                checked={data.expense?.hasOtherExpense || false}
                                onChange={(val) => {
                                  if (!val) {
                                    const newData = {
                                      ...data,
                                      expense: {
                                        ...data.expense,
                                        hasOtherExpense: false,
                                        otherExpenseName: undefined,
                                        otherExpenseAmount: undefined,
                                      },
                                    };
                                    onChange(newData);
                                    form.setFieldValue("expense", newData.expense);
                                  } else {
                                    handleNestedChange("expense", "hasOtherExpense", val);
                                  }
                                }}
                              />
                            </div>
                            {data.expense?.hasOtherExpense && (
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item label="名稱">
                                    <Input placeholder="請輸入" value={data.expense?.otherExpenseName} onChange={(e) => handleNestedChange("expense", "otherExpenseName", e.target.value)} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="金額">
                                    <AmountInput
                                      style={{ width: "100%" }}
                                      suffix={monthlyCurrencySuffix}
                                      suffixVariant={currencySuffixVariant}
                                      placeholder="請輸入"
                                      value={data.expense?.otherExpenseAmount ?? 0}
                                      onChange={(val) => {
                                        handleNestedChange("expense", "otherExpenseAmount", val ?? 0);
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      )}

                      {isZuuFinancialStepMode && !isRetiredInStep1 && (
                        <div style={{ marginTop: 20 }}>
                          <div style={subSectionHeaderStyle}>
                            <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                              1c. 於到達退休年齡後，您預期的平均每月可動用收入為？
                            </Title>
                          </div>
                          <div className={styles.zuuQuestionNote}>
                            一般而言，退休後的可動用收入可經由不同方式獲得，例如：銀行存款及債券所產生之利息、股票股息、租金收入、從生意營運所得的收入，以及／或從家庭成員或信託所得的收入等。
                          </div>
                          <div className={styles.zuuQuestionWarning}>*不適用於退休人士</div>
                          <div className={styles.zuuPendingField}>
                            <AmountInput
                              style={{ width: "100%" }}
                              suffix={monthlyCurrencySuffix}
                              suffixVariant={currencySuffixVariant}
                              placeholder="請輸入"
                              value={zuuStep2Draft.financialInfo.availableIncome.monthlyDisposableIncomeAfterRetirement.amount ?? undefined}
                              onChange={(val) => updateZuuDraftFreeAmount("availableIncome", "monthlyDisposableIncomeAfterRetirement", val)}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 2. 資產及負債 */}
                    <div style={headerBoxStyle}>
                      <span>{isZuuFinancialStepMode ? "2. 流動資產" : "2. 資產及負債 (HK$/港幣)"}</span>
                      <span
                        style={
                          isZuuFinancialStepMode
                            ? {
                                fontSize: 11,
                                color: "#5b6f87",
                                fontWeight: 500,
                              }
                            : undefined
                        }
                      >
                        {isZuuFinancialStepMode ? "＊流動資產需按投資可隨時變現部分及退休後額外流動資產估算" : `C+D-E = ${netAssets.toLocaleString()} HKD`}
                      </span>
                    </div>

                    <div style={{ marginBottom: 24, borderRadius: 6 }}>
                      {/* Current Assets (C) */}
                      <div style={subSectionHeaderStyle}>
                        <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                          {isZuuFinancialStepMode ? "2a. 您現時累積的流動資產約有多少？" : "C) 流動資產"}
                        </Title>
                        {!isZuuFinancialStepMode && <span style={{ color: "#333", fontWeight: 500 }}>小計:{totalCurrentAssets.toLocaleString()} HKD</span>}
                      </div>
                      {isZuuFinancialStepMode && <div className={styles.zuuQuestionNote}>流動資產是指可以容易變現為現金的資產。物業、藝術收藏品及藝術品均不能被視為流動資產。</div>}
                      {isZuuFinancialStepMode && (
                        <>
                          <div className={styles.zuuSummaryField}>
                            <AmountInput
                              style={{ width: "100%" }}
                              suffix={currencySuffix}
                              suffixVariant={currencySuffixVariant}
                              value={zuuStep2Draft.financialInfo.liquidAssets.currentLiquidAssets.amount}
                              placeholder="請輸入"
                              onChange={(val) => updateZuuDraftAmount("liquidAssets", "currentLiquidAssets", val)}
                            />
                          </div>
                        </>
                      )}
                      {!isZuuFinancialStepMode && (
                        <Row gutter={24}>
                          <Col span={12}>
                            <Form.Item label="現金及存款">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.currentAssets?.cashAndDeposits ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("currentAssets", "cashAndDeposits", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="變現能力高的投資 (股票/債券等)">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                suffixVariant={currencySuffixVariant}
                                placeholder="請輸入"
                                value={data.currentAssets?.liquidInvestments ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("currentAssets", "liquidInvestments", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 8,
                              }}
                            >
                              <Text>是否有其他流動資產 ?</Text>
                              <Switch
                                checkedChildren="是"
                                unCheckedChildren="否"
                                checked={data.currentAssets?.hasOtherCurrentAssets || false}
                                onChange={(val) => {
                                  if (!val) {
                                    const newData = {
                                      ...data,
                                      currentAssets: {
                                        ...data.currentAssets,
                                        hasOtherCurrentAssets: false,
                                        otherCurrentAssetsName: undefined,
                                        otherCurrentAssetsAmount: undefined,
                                      },
                                    };
                                    onChange(newData);
                                    form.setFieldValue("currentAssets", newData.currentAssets);
                                  } else {
                                    handleNestedChange("currentAssets", "hasOtherCurrentAssets", val);
                                  }
                                }}
                              />
                            </div>
                            {data.currentAssets?.hasOtherCurrentAssets && (
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item label="名稱">
                                    <Input
                                      placeholder="請輸入"
                                      value={data.currentAssets?.otherCurrentAssetsName}
                                      onChange={(e) => handleNestedChange("currentAssets", "otherCurrentAssetsName", e.target.value)}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="金額">
                                    <AmountInput
                                      style={{ width: "100%" }}
                                      suffix={currencySuffix}
                                      suffixVariant={currencySuffixVariant}
                                      placeholder="請輸入"
                                      value={data.currentAssets?.otherCurrentAssetsAmount ?? 0}
                                      onChange={(val) => {
                                        handleNestedChange("currentAssets", "otherCurrentAssetsAmount", val ?? 0);
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      )}

                      {isZuuFinancialStepMode && !isRetiredInStep1 && (
                        <div style={{ marginTop: 20 }}>
                          <div style={subSectionHeaderStyle}>
                            <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                              2b. 除上述2a的金額，您預期到退休年齡後額外獲得的流動資產約有多少？
                            </Title>
                          </div>
                          <div className={styles.zuuQuestionNote}>一般而言，退休後的流動資產可經由不同方式獲得，例如：強積金或退休儲蓄、銀行存款、股票或債券、遺產，以及從保單的保證紅利／現金等。</div>
                          <div className={styles.zuuQuestionWarning}>*不適用於退休人士</div>
                          <div className={styles.zuuPendingField}>
                            <AmountInput
                              style={{ width: "100%" }}
                              suffix={currencySuffix}
                              suffixVariant={currencySuffixVariant}
                              placeholder="請輸入"
                              value={zuuStep2Draft.financialInfo.liquidAssets.additionalLiquidAssetsAfterRetirement.amount ?? undefined}
                              onChange={(val) => updateZuuDraftFreeAmount("liquidAssets", "additionalLiquidAssetsAfterRetirement", val)}
                            />
                          </div>
                        </div>
                      )}

                      {!isZuuFinancialStepMode && (
                        <>
                          {/* Non-Current Assets (D) */}
                          <Divider style={{ margin: "24px 0" }} />
                          <div style={subSectionHeaderStyle}>
                            <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                              D) 非流動資產
                            </Title>
                            <span style={{ color: "#333", fontWeight: 500 }}>小計:{totalNonCurrentAssets.toLocaleString()} HKD</span>
                          </div>
                          <Row gutter={24}>
                            <Col span={12}>
                              <Form.Item label="物業">
                                <AmountInput
                                  style={{ width: "100%" }}
                                  suffix={currencySuffix}
                                  suffixVariant={currencySuffixVariant}
                                  placeholder="請輸入"
                                  value={data.nonCurrentAssets?.property ?? 0}
                                  onChange={(val) => {
                                    handleNestedChange("nonCurrentAssets", "property", val ?? 0);
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="其他 (如公司股份)">
                                <AmountInput
                                  style={{ width: "100%" }}
                                  suffix={currencySuffix}
                                  suffixVariant={currencySuffixVariant}
                                  placeholder="請輸入"
                                  value={data.nonCurrentAssets?.otherNonCurrentAssets ?? 0}
                                  onChange={(val) => {
                                    handleNestedChange("nonCurrentAssets", "otherNonCurrentAssets", val ?? 0);
                                  }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>

                          {/* Liabilities (E) */}
                          <Divider style={{ margin: "24px 0" }} />
                          <div style={subSectionHeaderStyle}>
                            <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                              E) 負債
                            </Title>
                            <span style={{ color: "#333", fontWeight: 500 }}>小計:{totalLiabilities.toLocaleString()} HKD</span>
                          </div>
                          <Row gutter={24}>
                            <Col span={12}>
                              <Form.Item label="按揭貸款">
                                <AmountInput
                                  style={{ width: "100%" }}
                                  suffix={currencySuffix}
                                  suffixVariant={currencySuffixVariant}
                                  placeholder="請輸入"
                                  value={data.liabilities?.mortgageLoan ?? 0}
                                  onChange={(val) => {
                                    handleNestedChange("liabilities", "mortgageLoan", val ?? 0);
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="私人/信用卡貸款">
                                <AmountInput
                                  style={{ width: "100%" }}
                                  suffix={currencySuffix}
                                  suffixVariant={currencySuffixVariant}
                                  placeholder="請輸入"
                                  value={data.liabilities?.personalCreditCardLoan ?? 0}
                                  onChange={(val) => {
                                    handleNestedChange("liabilities", "personalCreditCardLoan", val ?? 0);
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={24}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 12,
                                  marginBottom: 8,
                                }}
                              >
                                <Text>是否有其他負債 ?</Text>
                                <Switch
                                  checkedChildren="是"
                                  unCheckedChildren="否"
                                  checked={data.liabilities?.hasOtherLiabilities || false}
                                  onChange={(val) => {
                                    if (!val) {
                                      const newData = {
                                        ...data,
                                        liabilities: {
                                          ...data.liabilities,
                                          hasOtherLiabilities: false,
                                          otherLiabilityName: undefined,
                                          otherLiabilityAmount: undefined,
                                        },
                                      };
                                      onChange(newData);
                                      form.setFieldValue("liabilities", newData.liabilities);
                                    } else {
                                      handleNestedChange("liabilities", "hasOtherLiabilities", val);
                                    }
                                  }}
                                />
                              </div>
                              {data.liabilities?.hasOtherLiabilities && (
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Form.Item label="名稱">
                                      <Input
                                        placeholder="請輸入"
                                        value={data.liabilities?.otherLiabilityName}
                                        onChange={(e) => handleNestedChange("liabilities", "otherLiabilityName", e.target.value)}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item label="金額">
                                      <AmountInput
                                        style={{ width: "100%" }}
                                        suffix={currencySuffix}
                                        suffixVariant={currencySuffixVariant}
                                        placeholder="請輸入"
                                        value={data.liabilities?.otherLiabilityAmount ?? 0}
                                        onChange={(val) => {
                                          handleNestedChange("liabilities", "otherLiabilityAmount", val ?? 0);
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              )}
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  </CustomCard>

                  {!isZuuFinancialStepMode && (
                    <CustomCard title="二、現行保障 (HK$/港幣)" className={styles.sectionCard}>
                      <div style={{ marginBottom: 24, borderRadius: 6 }}>
                        <Row gutter={24}>
                          <Col span={12}>
                            <Form.Item label="人壽保險">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                placeholder="請輸入"
                                value={data.protection?.lifeInsurance ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("protection", "lifeInsurance", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="危疾保險">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                placeholder="請輸入"
                                value={data.protection?.criticalIllnessInsurance ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("protection", "criticalIllnessInsurance", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="醫療保險類別">
                              <SelectInput
                                options={getOptions("MEDICAL_INSURANCE_TYPE", [])}
                                value={data.protection?.medicalInsuranceType}
                                onChange={(val) => handleNestedChange("protection", "medicalInsuranceType", val ?? 0)}
                                title="醫療保險類別"
                                readOnly={readOnly}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="醫療保險金額">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                placeholder="請輸入"
                                value={data.protection?.medicalInsuranceAmount ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("protection", "medicalInsuranceAmount", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="意外保險">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                placeholder="請輸入"
                                value={data.protection?.accidentInsurance ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("protection", "accidentInsurance", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="其他現有保障">
                              <AmountInput
                                style={{ width: "100%" }}
                                suffix={currencySuffix}
                                placeholder="請輸入"
                                value={data.protection?.otherExistingProtection ?? 0}
                                onChange={(val) => {
                                  handleNestedChange("protection", "otherExistingProtection", val ?? 0);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="附件資料/備註">
                              <Input placeholder="請輸入" value={data.protection?.attachmentRemarks} onChange={(e) => handleNestedChange("protection", "attachmentRemarks", e.target.value)} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </CustomCard>
                  )}
                </>
              </Form>
            )}
          </>
        )}

        {activeSection === "fna" && !!orderId && (
          <>
            {false && isZuuFinancialStepMode && zuuReferenceLatestFna ? (
              <CustomCard title="引用最近一份 FNA" className={cx(styles.sectionCard, styles.zuuSectionCard)}>
                <div className={styles.zuuAdvisoryPanel}>
                  <div className={styles.zuuReferenceMeta}>
                    最近 FNA
                    {zuuReferenceLatestFna.orderNo ? `：訂單號 ${zuuReferenceLatestFna.orderNo}` : ""}
                    {zuuReferenceLatestFna.signTime ? `，更新時間 ${zuuReferenceLatestFna.signTime}` : ""}
                  </div>
                  <div className={styles.zuuAdvisoryNote}>只會引用最近一份 FNA 的分析內容，不會覆蓋「產品介紹及選擇」。</div>
                  <div>
                    <CustomButton onClick={zuuReferenceLatestFna.onReference}>引用最近一份 FNA</CustomButton>
                  </div>
                </div>
              </CustomCard>
            ) : null}
            <FnaFormComponent
              form={form}
              readOnly={readOnly}
              detailDisplay={detailDisplay}
              className={cx(styles.financialForm, isZuuFinancialStepMode && styles.zuuFinancialStepForm, detailDisplay && styles.zuuDetailDisplayForm)}
              zuuFinancialAnalysisProps={zuuFinancialAnalysisProps}
              zuuPremiumFinancingProps={zuuPremiumFinancingProps}
              zuuProductSelectionProps={zuuProductSelectionProps}
              zuuOnlyProvideOneProductProps={zuuOnlyProvideOneProductProps}
              zuuSuggestionsProps={zuuSuggestionsProps}
            >
              {isZuuFinancialStepMode && premiumCapacityWarnings.length > 0 ? (
                <CustomCard title="保費繳付能力提示" className={cx(styles.sectionCard, styles.zuuSectionCard)}>
                  <div className={styles.zuuAdvisoryPanel}>
                    {premiumCapacityWarnings.map((warning) => (
                      <div key={warning} className={styles.zuuAdvisoryItem}>
                        {warning}
                      </div>
                    ))}
                    <div className={styles.zuuAdvisoryNote}>此提示只作提醒，不會阻止儲存或進入下一步。</div>
                  </div>
                </CustomCard>
              ) : null}
              <>
                {/* 3. FNA */}
                {!isZuuFinancialStepMode && (
                  <CustomCard
                    title={isZuuFinancialStepMode ? "財務需要分析" : "三、財務需要分析"}
                    className={cx(styles.sectionCard, isZuuFinancialStepMode && styles.zuuSectionCard)}
                    bodyStyle={{ paddingTop: 0 }}
                  >
                    <div style={{ marginBottom: 24, borderRadius: 6 }}>
                      <Form.Item label="1. 閣下購買保險產品的目標為何？ (勾選一項或多項)" name="insurancePurchaseObjectives" rules={[{ required: true, message: "請至少選擇一項" }]}>
                        <Checkbox.Group
                          style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                          }}
                          value={getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX") || []}
                          onChange={(checkedValues) => {
                            // 获取当前完整的 financialAnalysis 数据
                            const currentFinancialAnalysis = [...(data.financialAnalysis || [])];

                            // 1. 更新多选框问题
                            const objectivesIndex = currentFinancialAnalysis.findIndex((q) => q.question === "insurancePurchaseObjectives");
                            const keys = (checkedValues as string[]) || [];
                            const objectivesItems = keys.map((k) => ({
                              key: k,
                              value: true,
                            }));

                            if (objectivesIndex >= 0) {
                              currentFinancialAnalysis[objectivesIndex] = {
                                ...currentFinancialAnalysis[objectivesIndex],
                                items: objectivesItems,
                              };
                            } else {
                              currentFinancialAnalysis.push({
                                question: "insurancePurchaseObjectives",
                                type: "CHECKBOX",
                                items: objectivesItems,
                              });
                            }

                            // 2. 初始化新选中选项的输入字段
                            const currentValues = getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX") || [];
                            const newlyChecked = keys.filter((v) => !currentValues.includes(v));

                            newlyChecked.forEach((option) => {
                              let inputQuestionKey = "";
                              if (option === "A") {
                                inputQuestionKey = "additionalLifeCoverageAmount";
                              } else if (option === "B") {
                                inputQuestionKey = "additionalCriticalIllnessCoverageAmount";
                              } else if (option === "C" || option === "D") {
                                inputQuestionKey = "additionalSavingsTargetReturnAmount";
                              } else if (option === "E") {
                                inputQuestionKey = "additionalInvestmentTargetReturnAmount";
                              }

                              if (inputQuestionKey) {
                                const existingIndex = currentFinancialAnalysis.findIndex((q) => q.question === inputQuestionKey);
                                if (existingIndex < 0) {
                                  // 只有不存在时才创建
                                  currentFinancialAnalysis.push({
                                    question: inputQuestionKey,
                                    type: "INPUT",
                                    items: [{ key: "A", value: 0 }],
                                  });
                                }
                              }
                            });

                            // 2.5. 清除取消勾选选项的字段
                            const newlyUnchecked = currentValues.filter((v) => !keys.includes(v));

                            newlyUnchecked.forEach((option) => {
                              const fieldsToClear: string[] = [];
                              if (option === "A") {
                                fieldsToClear.push("additionalLifeCoverageAmount");
                              } else if (option === "B") {
                                fieldsToClear.push("additionalCriticalIllnessCoverageAmount");
                              } else if (option === "C" || option === "D") {
                                // 只有C和D都未勾选时才清除共用字段
                                const otherOption = option === "C" ? "D" : "C";
                                if (!keys.includes(otherOption)) {
                                  fieldsToClear.push("additionalSavingsTargetReturnPeriod", "additionalSavingsTargetReturnAmount");
                                }
                              } else if (option === "E") {
                                fieldsToClear.push("additionalInvestmentTargetReturnPeriod", "additionalInvestmentTargetReturnAmount");
                              } else if (option === "F") {
                                fieldsToClear.push("insurancePurchaseOtherObjectives");
                              }

                              fieldsToClear.forEach((fieldKey) => {
                                const idx = currentFinancialAnalysis.findIndex((q) => q.question === fieldKey);
                                if (idx >= 0) {
                                  currentFinancialAnalysis[idx] = {
                                    ...currentFinancialAnalysis[idx],
                                    items: [],
                                  };
                                }
                                form.setFieldValue(fieldKey, undefined);
                              });
                            });

                            // 3. 一次性更新所有数据
                            onChange({
                              ...data,
                              financialAnalysis: currentFinancialAnalysis,
                            });

                            // 4. 同步更新 Form 字段值
                            form.setFieldValue("insurancePurchaseObjectives", checkedValues);
                          }}
                        >
                          {/* A */}
                          <div>
                            <Checkbox value="A" style={{ marginBottom: 8 }}>
                              A. 為應付不時之需提供財務保障(如死亡、意外、殘疾)
                            </Checkbox>
                            {(getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX") || []).includes("A") && (
                              <div style={insetPanelStyle}>
                                <Form.Item
                                  label="額外人壽保障額 (HK$/港幣)"
                                  name="additionalLifeCoverageAmount"
                                  rules={[
                                    {
                                      required: true,
                                      message: "請輸入額外人壽保障額",
                                    },
                                  ]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <AmountInput
                                    style={{ width: "100%" }}
                                    placeholder="請輸入"
                                    value={getQuestionValue("financialAnalysis", "additionalLifeCoverageAmount", "INPUT") ?? 0}
                                    onChange={(val) => {
                                      updateQuestionValue("financialAnalysis", "additionalLifeCoverageAmount", val ?? 0, "INPUT");
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            )}
                          </div>

                          {/* B */}
                          <div>
                            <Checkbox value="B" style={{ marginBottom: 8 }}>
                              B. 為醫療需要作準備(例如：危疾，住院等)
                            </Checkbox>
                            {(getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX") || []).includes("B") && (
                              <div style={insetPanelStyle}>
                                <Form.Item
                                  label="額外危疾保障額 (HK$/港幣)"
                                  name="additionalCriticalIllnessCoverageAmount"
                                  rules={[
                                    {
                                      required: true,
                                      message: "請輸入額外危疾保障額",
                                    },
                                  ]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <AmountInput
                                    style={{ width: "100%" }}
                                    placeholder="請輸入"
                                    value={getQuestionValue("financialAnalysis", "additionalCriticalIllnessCoverageAmount", "INPUT") ?? 0}
                                    onChange={(val) => {
                                      updateQuestionValue("financialAnalysis", "additionalCriticalIllnessCoverageAmount", val ?? 0, "INPUT");
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            )}
                          </div>

                          {/* C */}
                          <div>
                            <Checkbox value="C" style={{ marginBottom: 8 }}>
                              C. 為未來提供定期的收入(如退休收入)
                            </Checkbox>
                            {(getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX") || []).includes("C") && (
                              <div style={insetPanelStyle}>
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Form.Item
                                      label="年齡/年期"
                                      name="additionalSavingsTargetReturnPeriod"
                                      rules={[
                                        {
                                          required: true,
                                          message: "請輸入年齡/年期",
                                        },
                                      ]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input
                                        placeholder="請輸入"
                                        type="number"
                                        value={getQuestionValue("financialAnalysis", "additionalSavingsTargetReturnPeriod", "INPUT")}
                                        onChange={(e) => updateQuestionValue("financialAnalysis", "additionalSavingsTargetReturnPeriod", e.target.value, "INPUT")}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item
                                      label="額外回報金額(HK$/港幣)"
                                      name="additionalSavingsTargetReturnAmount"
                                      rules={[
                                        {
                                          required: true,
                                          message: "請輸入額外回報金額",
                                        },
                                      ]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <AmountInput
                                        style={{ width: "100%" }}
                                        placeholder="請輸入"
                                        value={getQuestionValue("financialAnalysis", "additionalSavingsTargetReturnAmount", "INPUT") ?? 0}
                                        onChange={(val) => {
                                          updateQuestionValue("financialAnalysis", "additionalSavingsTargetReturnAmount", val ?? 0, "INPUT");
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </div>

                          {/* D */}
                          <div>
                            <Checkbox value="D" style={{ marginBottom: 8 }}>
                              D. 為未來需要做儲蓄(如子女教育、退休)
                            </Checkbox>
                            {(getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX") || []).includes("D") && (
                              <div style={insetPanelStyle}>
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Form.Item
                                      label="年齡/年期"
                                      name="additionalSavingsTargetReturnPeriod"
                                      rules={[
                                        {
                                          required: true,
                                          message: "請輸入年齡/年期",
                                        },
                                      ]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input
                                        placeholder="請輸入"
                                        value={getQuestionValue("financialAnalysis", "additionalSavingsTargetReturnPeriod", "INPUT")}
                                        onChange={(e) => updateQuestionValue("financialAnalysis", "additionalSavingsTargetReturnPeriod", e.target.value, "INPUT")}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item
                                      label="額外回報金額(HK$/港幣)"
                                      name="additionalSavingsTargetReturnAmount"
                                      rules={[
                                        {
                                          required: true,
                                          message: "請輸入額外回報金額",
                                        },
                                      ]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <AmountInput
                                        style={{ width: "100%" }}
                                        placeholder="請輸入"
                                        value={getQuestionValue("financialAnalysis", "additionalSavingsTargetReturnAmount", "INPUT") ?? 0}
                                        onChange={(val) => {
                                          updateQuestionValue("financialAnalysis", "additionalSavingsTargetReturnAmount", val ?? 0, "INPUT");
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </div>

                          {/* E */}
                          <div>
                            <Checkbox value="E" style={{ marginBottom: 8 }}>
                              E. 投資
                            </Checkbox>
                            {(getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX") || []).includes("E") && (
                              <div style={insetPanelStyle}>
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Form.Item
                                      label="年齡/年期"
                                      name="additionalInvestmentTargetReturnPeriod"
                                      rules={[
                                        {
                                          required: true,
                                          message: "請輸入年齡/年期",
                                        },
                                      ]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input
                                        placeholder="請輸入"
                                        type="number"
                                        value={getQuestionValue("financialAnalysis", "additionalInvestmentTargetReturnPeriod", "INPUT") ?? 0}
                                        onChange={(e) => updateQuestionValue("financialAnalysis", "additionalInvestmentTargetReturnPeriod", e.target.value ?? 0, "INPUT")}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item
                                      label="額外回報金額(HK$/港幣)"
                                      name="additionalInvestmentTargetReturnAmount"
                                      rules={[
                                        {
                                          required: true,
                                          message: "請輸入額外回報金額",
                                        },
                                      ]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <AmountInput
                                        style={{ width: "100%" }}
                                        placeholder="請輸入"
                                        value={getQuestionValue("financialAnalysis", "additionalInvestmentTargetReturnAmount", "INPUT") ?? 0}
                                        onChange={(val) => {
                                          updateQuestionValue("financialAnalysis", "additionalInvestmentTargetReturnAmount", val ?? 0, "INPUT");
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </div>

                          {/* F */}
                          <div>
                            <Checkbox value="F" style={{ marginBottom: 8 }}>
                              F. 其他
                            </Checkbox>
                            {(getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX") || []).includes("F") && (
                              <div style={insetPanelStyle}>
                                <Form.Item label="請詳述" name="insurancePurchaseOtherObjectives" rules={[{ required: true, message: "請輸入詳細內容" }]} style={{ marginBottom: 0 }}>
                                  <TextArea
                                    placeholder="請輸入詳細內容"
                                    value={getQuestionValue("financialAnalysis", "insurancePurchaseOtherObjectives", "INPUT")}
                                    onChange={(e) => updateQuestionValue("financialAnalysis", "insurancePurchaseOtherObjectives", e.target.value, "INPUT")}
                                  />
                                </Form.Item>
                              </div>
                            )}
                          </div>
                        </Checkbox.Group>
                        {(getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX") || []).includes("E") && (
                          <Form.Item
                            style={{ marginTop: "48px" }}
                            label="為實現上述「投資」的目標，閣下希望如何管理保險產品項目下的不同投資選項 / 投資選擇（如有）？（勾選一項）。"
                            name="investmentOptionManagementPreference"
                            rules={[
                              {
                                required: true,
                                message: "請回答：為實現上述「E.投資」的目標，閣下希望如何管理保險產品項目下的不同投資選項 / 投資選擇。 ",
                              },
                            ]}
                          >
                            <Radio.Group
                              style={{ width: "100%" }}
                              value={getQuestionValue("financialAnalysis", "investmentOptionManagementPreference", "RADIO")}
                              onChange={(e) => updateQuestionValue("financialAnalysis", "investmentOptionManagementPreference", e.target.value, "RADIO")}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 12,
                                }}
                              >
                                <Radio
                                  value="A"
                                  style={{
                                    whiteSpace: "normal",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  本人願意按個人決定選擇及管理保險產品項下的不同投資選項/投資選擇（如有），並且願意在保險產品的目標利益/保障期的整個期間作出此決定。（若選擇此項，必須要回答問題1.E.1.1、1.E.1.2、1.E.1.3）
                                </Radio>

                                <Radio
                                  value="B"
                                  style={{
                                    whiteSpace: "normal",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  本人願意按個人決定（經獲授權保險人及/或持牌保險中介人提供專業意見的情況）選擇及管理保險產品項下的不同投資選項/投資選擇（如有），並且願意在保險產品的目標利益/保障期的整個期間作出此決定。
                                </Radio>

                                <Radio
                                  value="C"
                                  style={{
                                    whiteSpace: "normal",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  本人不願意選擇或管理保險產品項下的不同投資選項/投資選擇（如有）。
                                </Radio>
                              </div>
                            </Radio.Group>
                          </Form.Item>
                        )}
                        {getQuestionValue("financialAnalysis", "investmentOptionManagementPreference", "RADIO") &&
                          getQuestionValue("financialAnalysis", "investmentOptionManagementPreference", "RADIO") !== "C" && (
                            <div
                              style={{
                                ...neutralInsetPanelStyle,
                                marginLeft: isZuuFinancialStepMode ? 0 : 24,
                                display: "flex",
                                flexDirection: "column",
                                gap: 12,
                              }}
                            >
                              <Form.Item label="1.E.1.1 閣下計劃何時開始提取閣下的投資？(勾選一項)。" name="investmentWithdrawalStartTime" rules={[{ required: true }]}>
                                <Radio.Group
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 12,
                                  }}
                                  value={getQuestionValue("financialAnalysis", "investmentWithdrawalStartTime", "RADIO")}
                                  onChange={(e) => updateQuestionValue("financialAnalysis", "investmentWithdrawalStartTime", e.target.value, "RADIO")}
                                >
                                  <Radio value="A">20年後</Radio>
                                  <Radio value="B">十至二十年內</Radio>
                                  <Radio value="C">五至十年內</Radio>
                                  <Radio value="D">五年內但現在不提取</Radio>
                                  <Radio value="E">立即提取</Radio>
                                </Radio.Group>
                              </Form.Item>

                              <Form.Item label="1.E.1.2 閣下對這項投資的長期目標是什麼？(勾選一項)。" name="investmentLongTermGoal" rules={[{ required: true }]}>
                                <Radio.Group
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 12,
                                  }}
                                  value={getQuestionValue("financialAnalysis", "investmentLongTermGoal", "RADIO")}
                                  onChange={(e) => updateQuestionValue("financialAnalysis", "investmentLongTermGoal", e.target.value, "RADIO")}
                                >
                                  <Radio value="A">積極增長</Radio>
                                  <Radio value="B">顯著增長</Radio>
                                  <Radio value="C">適度增長</Radio>
                                  <Radio value="D">輕微增長</Radio>
                                  <Radio value="E">避免虧損</Radio>
                                </Radio.Group>
                              </Form.Item>

                              <Form.Item label="1.E.1.3 如果未來10年股市表現不佳，閣下對這項投資的期望是什麼？（勾選一項）。" name="investmentExpectationIfMarketPoor" rules={[{ required: true }]}>
                                <Radio.Group
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 12,
                                  }}
                                  value={getQuestionValue("financialAnalysis", "investmentExpectationIfMarketPoor", "RADIO")}
                                  onChange={(e) => updateQuestionValue("financialAnalysis", "investmentExpectationIfMarketPoor", e.target.value, "RADIO")}
                                >
                                  <Radio value="A">虧損</Radio>
                                  <Radio value="B">微小收益或無收益</Radio>
                                  <Radio value="C">小幅收益</Radio>
                                  <Radio value="D">適度收益</Radio>
                                  <Radio value="E">輕微受市場影響</Radio>
                                </Radio.Group>
                              </Form.Item>
                            </div>
                          )}
                      </Form.Item>

                      <Form.Item label="5. 您投購保單的目標得益／保障年期／實現目標金額的預期時間為多久？（勾選一項）。" name="policyTargetExpectedTime" rules={[{ required: true }]}>
                        <Radio.Group
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                          }}
                          value={getQuestionValue("financialAnalysis", "policyTargetExpectedTime", "RADIO")}
                          onChange={(e) => updateQuestionValue("financialAnalysis", "policyTargetExpectedTime", e.target.value, "RADIO")}
                        >
                          <Radio value="A">少於1年</Radio>
                          <Radio value="B">1-5年</Radio>
                          <Radio value="C">6-10年</Radio>
                          <Radio value="D">11-15年</Radio>
                          <Radio value="E">16-20年</Radio>
                          <Radio value="F">超過20年</Radio>
                          <Radio value="G">終身</Radio>
                        </Radio.Group>
                      </Form.Item>

                      {/* 只有选择 G. 終身 时才显示 3.1 */}
                      <Form.Item
                        label="2. 在整個保單期內，閣下能夠及願意繳付的保費（不包括閣下現有的其他保單）佔透過所有收入來源（包括流動資產收入）獲得的每月可動用收入（即經扣除開支）的比率為？（勾選一項）。"
                        name="premiumToDisposableIncomeRatio"
                        rules={[{ required: true }]}
                      >
                        <Radio.Group
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                          }}
                          value={getQuestionValue("financialAnalysis", "premiumToDisposableIncomeRatio", "RADIO")}
                          onChange={(e) => updateQuestionValue("financialAnalysis", "premiumToDisposableIncomeRatio", e.target.value, "RADIO")}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <Radio value="A">A. ≤10%</Radio>
                          </div>
                          <Radio value="B">B. 11%-20%</Radio>
                          <Radio value="C">C. 21%-40%</Radio>
                          <Radio value="D">D. 41%-60%</Radio>
                          <Radio value="E">E. 61%-80%</Radio>
                          <Radio value="F">F. 81%-100%</Radio>
                        </Radio.Group>
                      </Form.Item>

                      <Form.Item
                        label="3. 在整個保單期內，閣下能夠及願意繳付的保費（不包括閣下現有的其他保單）佔流動資產比率為？（勾選一項）。"
                        name="premiumToLiquidAssetsRatio"
                        rules={[{ required: true }]}
                      >
                        <Radio.Group
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                          }}
                          value={getQuestionValue("financialAnalysis", "premiumToLiquidAssetsRatio", "RADIO")}
                          onChange={(e) => updateQuestionValue("financialAnalysis", "premiumToLiquidAssetsRatio", e.target.value, "RADIO")}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <Radio value="A">A. ≤10%</Radio>
                          </div>
                          <Radio value="B">B. 11%-20%</Radio>
                          <Radio value="C">C. 21%-40%</Radio>
                          <Radio value="D">D. 41%-60%</Radio>
                          <Radio value="E">E. 61%-80%</Radio>
                          <Radio value="F">F. 81%-100%</Radio>
                        </Radio.Group>
                      </Form.Item>

                      <Form.Item label="3.3 閣下交付保費的能力： 阁下打算以何种方式支付保费？" name="intendedPremiumPaymentMethods" rules={[{ required: true, message: "請至少選擇一項" }]}>
                        <Checkbox.Group
                          style={{ display: "flex", gap: 24 }}
                          value={getQuestionValue("financialAnalysis", "intendedPremiumPaymentMethods", "CHECKBOX") || []}
                          onChange={(checkedValues) => {
                            updateQuestionValue("financialAnalysis", "intendedPremiumPaymentMethods", checkedValues, "CHECKBOX");
                            // 同步更新 Form 字段值
                            form.setFieldValue("intendedPremiumPaymentMethods", checkedValues);
                          }}
                        >
                          <Checkbox value="A">A. 每月所有收入來源即經扣除開支的可動用收入</Checkbox>
                          <Checkbox value="B">B. 流動資產</Checkbox>
                        </Checkbox.Group>
                      </Form.Item>

                      {/* 只在选中了选项 A 或 B 时才显示这个灰色背景区域 */}
                      {((getQuestionValue("financialAnalysis", "intendedPremiumPaymentMethods", "CHECKBOX") || []).includes("A") ||
                        (getQuestionValue("financialAnalysis", "intendedPremiumPaymentMethods", "CHECKBOX") || []).includes("B")) && (
                        <div style={neutralInsetPanelStyle}>
                          {/* Only show a, a1 when option A is selected */}
                          {(getQuestionValue("financialAnalysis", "intendedPremiumPaymentMethods", "CHECKBOX") || []).includes("A") && (
                            <>
                              <div style={{ marginBottom: 8 }}>
                                a. 每月可動用收入: {formatInteger(a_monthly)} HKD/月 {formatInteger(a_yearly)} HKD/年
                              </div>
                              <div style={{ marginBottom: 8 }}>
                                a1. 每月可動用收入願意繳付保費的最高金額: {formatCurrency(a1_monthly)} HKD/月 {formatCurrency(a1_yearly)} HKD/年
                              </div>
                            </>
                          )}
                          {/* Only show b, b1 when option B is selected */}
                          {(getQuestionValue("financialAnalysis", "intendedPremiumPaymentMethods", "CHECKBOX") || []).includes("B") && (
                            <>
                              <div style={{ marginBottom: 8 }}>b. 流動資產: {formatInteger(b_assets)} HKD</div>
                              <div>b1. 流動資產願意繳付保費最高金額: {formatCurrency(b1_amount)} HKD</div>
                            </>
                          )}
                        </div>
                      )}
                      <div style={{ color: "red", marginTop: 8, fontSize: 14 }}>*請注意，閣下的繳費承擔能力，需要涵蓋正在申請和即將申請的保單總保費</div>

                      <Form.Item label="4. 閣下能夠及願意為保單支付保費的年期為？（勾選一項）" style={{ marginTop: 24 }} name="willingPremiumPaymentPeriodRange" rules={[{ required: true }]}>
                        <Radio.Group
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                          }}
                          value={getQuestionValue("financialAnalysis", "willingPremiumPaymentPeriodRange", "RADIO")}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            const currentFinancialAnalysis = [...(data.financialAnalysis || [])].filter((q) => q.question);

                            // 更新 willingPremiumPaymentPeriodRange
                            const periodRangeIndex = currentFinancialAnalysis.findIndex((q) => q.question === "willingPremiumPaymentPeriodRange");
                            const periodRangeItems = periodRangeIndex >= 0 ? currentFinancialAnalysis[periodRangeIndex].items || [] : [];
                            const updatedPeriodItems = periodRangeItems.map((i) => ({
                              ...i,
                              value: i.key === newValue,
                            }));
                            if (!updatedPeriodItems.find((i) => i.key === newValue)) {
                              updatedPeriodItems.push({ key: newValue, value: true });
                            }
                            if (periodRangeIndex >= 0) {
                              currentFinancialAnalysis[periodRangeIndex] = {
                                ...currentFinancialAnalysis[periodRangeIndex],
                                items: updatedPeriodItems,
                              };
                            } else {
                              currentFinancialAnalysis.push({
                                question: "willingPremiumPaymentPeriodRange",
                                type: "RADIO",
                                items: updatedPeriodItems,
                              });
                            }

                            // 当选择非 G 时,清空 singlePremiumPaymentLimit
                            if (newValue !== "G") {
                              const limitIndex = currentFinancialAnalysis.findIndex((q) => q.question === "singlePremiumPaymentLimit");
                              if (limitIndex >= 0) {
                                currentFinancialAnalysis[limitIndex] = {
                                  ...currentFinancialAnalysis[limitIndex],
                                  items: [{ key: "A", value: 0 }],
                                };
                              }
                              form.setFieldValue("singlePremiumPaymentLimit", 0);
                            }

                            onChange({ ...data, financialAnalysis: currentFinancialAnalysis });
                          }}
                        >
                          <Radio value="A">2-5年</Radio>
                          <Radio value="B">6-10年</Radio>
                          <Radio value="C">11-15年</Radio>
                          <Radio value="D">15-20年</Radio>
                          <Radio value="E">超過20年</Radio>
                          <Radio value="F">終身</Radio>
                          <Radio value="G">一次性供款不超過</Radio>
                        </Radio.Group>
                        {/* Only show input when option G is selected */}
                        {getQuestionValue("financialAnalysis", "willingPremiumPaymentPeriodRange", "RADIO") === "G" && (
                          <AmountInput
                            placeholder="請輸入金額"
                            addonAfter="HKD"
                            style={{ marginTop: 8, width: "100%" }}
                            value={getQuestionValue("financialAnalysis", "singlePremiumPaymentLimit", "INPUT") ?? 0}
                            onChange={(val) => {
                              updateQuestionValue("financialAnalysis", "singlePremiumPaymentLimit", val ?? 0, "INPUT");
                            }}
                          />
                        )}
                      </Form.Item>

                      {/* 问题 4.2 - 条件显示 */}
                      {shouldShowQuestion42() && (
                        <Form.Item label="4.2 如供款年期超過一般退休年歲或在保險建議項目上的期望退休年歲，請說明可以持續供款的原因。（勾選一項）">
                          <Radio.Group
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                            value={getQuestionValue("financialAnalysis", "continuedContributionReason", "RADIO")}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              const currentFinancialAnalysis = [...(data.financialAnalysis || [])].filter((q) => q.question);

                              // 更新 continuedContributionReason
                              const reasonIndex = currentFinancialAnalysis.findIndex((q) => q.question === "continuedContributionReason");
                              const reasonItems = reasonIndex >= 0 ? currentFinancialAnalysis[reasonIndex].items || [] : [];
                              const updatedReasonItems = reasonItems.map((i) => ({
                                ...i,
                                value: i.key === newValue,
                              }));
                              if (!updatedReasonItems.find((i) => i.key === newValue)) {
                                updatedReasonItems.push({ key: newValue, value: true });
                              }
                              if (reasonIndex >= 0) {
                                currentFinancialAnalysis[reasonIndex] = {
                                  ...currentFinancialAnalysis[reasonIndex],
                                  items: updatedReasonItems,
                                };
                              } else {
                                currentFinancialAnalysis.push({
                                  question: "continuedContributionReason",
                                  type: "RADIO",
                                  items: updatedReasonItems,
                                });
                              }

                              // 当选择非 B 时,清空 continuedContributionReasonOther
                              if (newValue !== "B") {
                                const otherIndex = currentFinancialAnalysis.findIndex((q) => q.question === "continuedContributionReasonOther");
                                if (otherIndex >= 0) {
                                  currentFinancialAnalysis[otherIndex] = {
                                    ...currentFinancialAnalysis[otherIndex],
                                    items: [{ key: "A", value: "" }],
                                  };
                                }
                                form.setFieldValue("continuedContributionReasonOther", undefined);
                              }

                              onChange({ ...data, financialAnalysis: currentFinancialAnalysis });
                            }}
                          >
                            <Radio value="A">保費會根據退休時的實際需要，由退休儲蓄金支付。</Radio>
                            <Radio value="B">其他</Radio>
                          </Radio.Group>
                          {/* Only show input when option B (其他) is selected */}
                          {getQuestionValue("financialAnalysis", "continuedContributionReason", "RADIO") === "B" && (
                            <Input
                              placeholder="請輸入原因"
                              style={{ marginTop: 8 }}
                              value={getQuestionValue("financialAnalysis", "continuedContributionReasonOther", "INPUT")}
                              onChange={(e) => updateQuestionValue("financialAnalysis", "continuedContributionReasonOther", e.target.value, "INPUT")}
                            />
                          )}
                        </Form.Item>
                      )}
                    </div>
                  </CustomCard>
                )}

                {/* 4. Premium Financing */}
                {!isZuuFinancialStepMode && (
                  <CustomCard title={isZuuFinancialStepMode ? "保費融資" : "四、保費融資"} className={cx(styles.sectionCard, isZuuFinancialStepMode && styles.zuuSectionCard)}>
                    <div style={{ marginBottom: 24, borderRadius: 6 }}>
                      <Form.Item label='1. 閣下是否打算運用"保費融資"安排此保單 ? (勾選一項)' name="isPremiumFinanced" rules={[{ required: true }]}>
                        <Radio.Group value={data.isPremiumFinanced} onChange={(e) => handleChange("isPremiumFinanced", e.target.value)} style={{ display: "flex", gap: 24 }}>
                          <Radio value={true}>A. 是</Radio>
                          <Radio value={false}>B. 否</Radio>
                        </Radio.Group>
                        <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 14 }}>
                          {isZuuFinancialStepMode ? "如果閣下不打算進行保費融資，則不需回答此部份。" : '若上方問題之答案為「是」者，請填寫附件"保費融資安排分析"。'}
                        </Text>
                      </Form.Item>

                      <Form.Item
                        label='2. 閣下是否有任何現有的保單被用作"保費融資"的抵押品或以其他方式質押或轉讓作為貸款工具的抵押品 ? (勾選一項)'
                        style={{ marginTop: 24 }}
                        name="hasExistingPolicyAsMortgage"
                        rules={[{ required: true }]}
                      >
                        <Radio.Group
                          value={data.hasExistingPolicyAsMortgage}
                          onChange={(e) => {
                            const val = e.target.value;
                            const newData = {
                              ...data,
                              hasExistingPolicyAsMortgage: val,
                            };
                            if (val === true && (!newData.premiumFinancing || newData.premiumFinancing.length === 0)) {
                              newData.premiumFinancing = [{}];
                            }
                            onChange(newData);
                          }}
                          style={{ display: "flex", gap: 24 }}
                        >
                          <Radio value={true}>A. 是</Radio>
                          <Radio value={false}>B. 否</Radio>
                        </Radio.Group>
                        <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 14 }}>
                          若上方問題之答案為「是」者，請填寫下列表格告知現有保費融資的抵押保單詳情。
                        </Text>
                      </Form.Item>

                      {data.hasExistingPolicyAsMortgage && (
                        <div style={{ marginTop: 24 }}>
                          {(data.premiumFinancing || []).map((item, index) => (
                            <div key={index} style={{ marginBottom: 24 }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 16,
                                }}
                              >
                                <Text strong style={{ fontSize: 14 }}>
                                  保單{index + 1}
                                </Text>
                                {(data.premiumFinancing || []).length > 1 && !readOnly && (
                                  <CustomButton bgColor="#ff4d4f" color="#fff" height={24} fontSize={12} onClick={() => handleRemovePremiumFinancing(index)}>
                                    刪除
                                  </CustomButton>
                                )}
                              </div>
                              <Row gutter={24}>
                                <Col span={12}>
                                  <Form.Item label="保險公司">
                                    <SelectInput
                                      options={companyList.map((company) => ({
                                        label: company.name || company.code,
                                        value: company.code,
                                      }))}
                                      value={item.insuranceCompany}
                                      onChange={(val) => handlePremiumFinancingChange(index, "insuranceCompany", val)}
                                      title="保險公司"
                                      readOnly={readOnly}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="保單號碼">
                                    <Input placeholder="請輸入" value={item.policyNo} onChange={(e) => handlePremiumFinancingChange(index, "policyNo", e.target.value)} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="貸方名稱">
                                    <Input placeholder="請輸入" value={item.lenderName} onChange={(e) => handlePremiumFinancingChange(index, "lenderName", e.target.value)} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="貸款總額">
                                    <Input placeholder="請輸入" value={item.loanAmount} onChange={(e) => handlePremiumFinancingChange(index, "loanAmount", e.target.value)} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="貸款利率">
                                    <Input placeholder="請輸入" suffix="%" value={item.loanInterestRate} onChange={(e) => handlePremiumFinancingChange(index, "loanInterestRate", e.target.value)} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="貸款期限">
                                    <Input placeholder="請輸入" value={item.loanTerm} onChange={(e) => handlePremiumFinancingChange(index, "loanTerm", e.target.value)} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="還款項">
                                    <Input placeholder="請輸入" value={item.repaymentAmount} onChange={(e) => handlePremiumFinancingChange(index, "repaymentAmount", e.target.value)} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item label="備註">
                                    <Input placeholder="請輸入" value={item.remark} onChange={(e) => handlePremiumFinancingChange(index, "remark", e.target.value)} />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </div>
                          ))}
                          {!readOnly && (
                            <CustomButton bgColor="var(--theme-primary)" color="#fff" icon={<PlusOutlined />} onClick={handleAddPremiumFinancing}>
                              新增抵押保單
                            </CustomButton>
                          )}
                        </div>
                      )}
                    </div>
                  </CustomCard>
                )}

                {/* 5. Transfer Insurance Declaration */}
                {!isZuuFinancialStepMode && (
                  <CustomCard title="五、轉保聲明" className={cx(styles.sectionCard, isZuuFinancialStepMode && styles.zuuSectionCard)}>
                    <div style={{ marginBottom: 24, borderRadius: 6 }}>
                      <Form.Item
                        label="1. 閣下是否使用或打算使用現有人壽保險保單的部分或全部資金，或使用或打算使用通過減少現有人壽保險保單的應付保費而節省的金額，以資助閣下購買新的人壽保險保單？ (勾選一項)"
                        name="usingExistingPolicyFundsForNewPolicy"
                        rules={[{ required: true }]}
                      >
                        <Radio.Group
                          value={getQuestionValue("transferInsuranceDeclaration", "usingExistingPolicyFundsForNewPolicy", "RADIO")}
                          onChange={(e) => updateQuestionValue("transferInsuranceDeclaration", "usingExistingPolicyFundsForNewPolicy", e.target.value, "RADIO")}
                        >
                          <Radio value="A">是</Radio>
                          <Radio value="B">尚未決定</Radio>
                          <Radio value="C">否</Radio>
                        </Radio.Group>
                      </Form.Item>
                      <div style={{ marginLeft: 24, fontSize: 12, color: "#666" }}>
                        <p>例如：此等資金或金額可能來自：</p>
                        <ul style={{ listStyleType: "none", padding: 0, fontSize: 14 }}>
                          <li>a) 就閣下現有人壽保險保單作出退保/不分退保的安排，已獲得其退保價值；</li>
                          <li>b) 從閣下現有人壽保險保單中提取保單貸款（包括自動保費貸款/抵押轉讓（不包括保費融資））；</li>
                          <li>c) 從閣下現有人壽保險保單中提取保單價值（例如如實行紅利或贖回基金單位等）；</li>
                          <li>d) 容許閣下現有人壽保險保單失效（例如終止止付保費）；</li>
                          <li>e) 行使閣下現有人壽保險保單中“保費假期”的權利。</li>
                        </ul>
                      </div>
                      <div style={{ color: "red", marginTop: 8, fontSize: 14 }}>*請注意，投保之後一年內在冷靜期之後退保的情況，亦有可能涉及轉保，請保險中介人清楚了解情況。</div>

                      {(getQuestionValue("transferInsuranceDeclaration", "usingExistingPolicyFundsForNewPolicy", "RADIO") === "A" ||
                        getQuestionValue("transferInsuranceDeclaration", "usingExistingPolicyFundsForNewPolicy", "RADIO") === "B") && (
                        <div
                          style={{
                            marginTop: 16,
                            padding: 16,
                            background: "#f5f5f5",
                            borderRadius: 6,
                          }}
                        >
                          <Form.Item label="1.A 若上方問題之答案為「是」或「尚未決定」者，保險中介人的評估和意見：" name="intermediaryAssessmentAndComments" rules={[{ required: true }]}>
                            <Radio.Group
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                              }}
                              value={getQuestionValue("transferInsuranceDeclaration", "intermediaryAssessmentAndComments", "RADIO")}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                const currentDeclaration = [...(data.transferInsuranceDeclaration || [])].filter((q) => q.question);

                                // 更新 intermediaryAssessmentAndComments
                                const assessIdx = currentDeclaration.findIndex((q) => q.question === "intermediaryAssessmentAndComments");
                                const assessItems = assessIdx >= 0 ? currentDeclaration[assessIdx].items || [] : [];
                                const updatedAssessItems = assessItems.map((i) => ({
                                  ...i,
                                  value: i.key === newValue,
                                }));
                                if (!updatedAssessItems.find((i) => i.key === newValue)) {
                                  updatedAssessItems.push({ key: newValue, value: true });
                                }
                                if (assessIdx >= 0) {
                                  currentDeclaration[assessIdx] = {
                                    ...currentDeclaration[assessIdx],
                                    items: updatedAssessItems,
                                  };
                                } else {
                                  currentDeclaration.push({
                                    question: "intermediaryAssessmentAndComments",
                                    type: "RADIO",
                                    items: updatedAssessItems,
                                  });
                                }

                                // 当选择非 B 时,清空 intermediaryAssessmentAndCommentsOther
                                if (newValue !== "B") {
                                  const otherIdx = currentDeclaration.findIndex((q) => q.question === "intermediaryAssessmentAndCommentsOther");
                                  if (otherIdx >= 0) {
                                    currentDeclaration[otherIdx] = {
                                      ...currentDeclaration[otherIdx],
                                      items: [{ key: "A", value: "" }],
                                    };
                                  }
                                  form.setFieldValue("intermediaryAssessmentAndCommentsOther", undefined);
                                }

                                onChange({ ...data, transferInsuranceDeclaration: currentDeclaration });
                              }}
                            >
                              <Radio
                                value="A"
                                style={{
                                  whiteSpace: "normal",
                                  alignItems: "flex-start",
                                }}
                              >
                                A. 在檢視了客戶現有的人壽保險之後，客戶認為新的計劃更為合適(如享有更佳的保障/更靈活的保費繳付模式/較大比例的保證回報/多元化產品特徵)，客人已清楚了解轉保的影響和相關風險
                              </Radio>
                              <Radio value="B">其他</Radio>
                            </Radio.Group>
                          </Form.Item>
                          {getQuestionValue("transferInsuranceDeclaration", "intermediaryAssessmentAndComments", "RADIO") === "B" && (
                            <Form.Item label="請輸入詳細內容" name="intermediaryAssessmentAndCommentsOther" rules={[{ required: true, message: "請輸入詳細內容" }]}>
                              <Input.TextArea
                                placeholder="請輸入"
                                rows={4}
                                value={getQuestionValue("transferInsuranceDeclaration", "intermediaryAssessmentAndCommentsOther", "INPUT")}
                                onChange={(e) => updateQuestionValue("transferInsuranceDeclaration", "intermediaryAssessmentAndCommentsOther", e.target.value, "INPUT")}
                              />
                            </Form.Item>
                          )}
                        </div>
                      )}
                    </div>
                  </CustomCard>
                )}

                {/* 6. Product Introduction & Selection Results */}
                {!isZuuFinancialStepMode && (
                  <CustomCard title={isZuuFinancialStepMode ? "產品介紹及選擇結果" : "六、產品介紹及選擇結果"} className={cx(styles.sectionCard, isZuuFinancialStepMode && styles.zuuSectionCard)}>
                    <div ref={productSectionRef}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 16,
                        }}
                      >
                        {/* Add Product Button - moved to top right */}
                        {!readOnly && (
                          <CustomButton
                            bgColor="var(--theme-primary)"
                            color="#fff"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              const newSelection = [...(data.chooseProducts || [])];
                              newSelection.push(newChooseProduct);
                              onChange({ ...data, chooseProducts: newSelection });
                            }}
                          >
                            新增產品
                          </CustomButton>
                        )}
                      </div>
                    </div>
                    <div style={{ marginBottom: 24, borderRadius: 6 }}>
                      <div style={{ fontSize: 12, marginBottom: 16, lineHeight: 1.6 }}>
                        <div style={{ color: "#333", marginBottom: 4 }}>曾介紹的保險公司及產品名稱</div>
                        <div style={{ color: "#ff4d4f" }}>*新增介紹的產品後，勾選的產品為最終投保的產品，最多可同時投保 6 個產品，如只是新增作介紹對比的產品而最終不購買，請勿勾選。</div>
                      </div>
                      <Row gutter={[24, 24]}>
                        {(() => {
                          // Helper to derive fields from FNA
                          const getDerivedFields = () => {
                            const q1 = getQuestionValue("financialAnalysis", "insurancePurchaseObjectives", "CHECKBOX") || [];

                            // Build objective string from all selected options
                            const objectiveLabels: string[] = [];
                            if (q1.includes("A")) objectiveLabels.push("為應付不時之需提供財務保障");
                            if (q1.includes("B")) objectiveLabels.push("為醫療需要作準備");
                            if (q1.includes("C")) objectiveLabels.push("為未來提供定期的收入");
                            if (q1.includes("D")) objectiveLabels.push("為未來需要做儲蓄");
                            if (q1.includes("E")) objectiveLabels.push("投資");
                            if (q1.includes("F")) objectiveLabels.push("其他");
                            const objective = objectiveLabels.join("、");

                            const q4_1 = getQuestionValue("financialAnalysis", "willingPremiumPaymentPeriodRange", "RADIO");
                            let paymentTerm = "";

                            if (q4_1) {
                              const paymentTermMap: Record<string, string> = {
                                A: "2-5年",
                                B: "6-10年",
                                C: "11-15年",
                                D: "16-20年",
                                E: "超過20年",
                                F: "終身",
                              };

                              if (q4_1 === "G") {
                                // For option G, show "一次性付款不超過 + amount"
                                const singlePaymentLimit = getQuestionValue("financialAnalysis", "singlePremiumPaymentLimit", "INPUT");
                                const formattedAmount = singlePaymentLimit ? Number(singlePaymentLimit).toLocaleString("en-US") : "0";
                                paymentTerm = `一次性付款不超過 ${formattedAmount} HKD`;
                              } else {
                                paymentTerm = paymentTermMap[q4_1] || "";
                              }
                            }

                            const benefitTermMap: Record<string, string> = {
                              A: "少於1年",
                              B: "1-5年",
                              C: "6-10年",
                              D: "11-15年",
                              E: "16-20年",
                              F: "超過20年",
                              G: "終身",
                            };
                            const q2 = getQuestionValue("financialAnalysis", "policyTargetExpectedTime", "RADIO");
                            const benefitTerm = q2 ? benefitTermMap[q2] : undefined;

                            return { objective, paymentTerm, benefitTerm };
                          };
                          const derived = getDerivedFields();
                          const productsToShow = [...(data.chooseProducts || [])];

                          // Count selected products
                          const selectedCount = productsToShow.filter((p) => p.choose).length;

                          // Show empty state if no products
                          if (productsToShow.length === 0) {
                            return <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>暫無產品，請點擊上方「新增產品」按鈕添加</div>;
                          }

                          return (
                            <>
                              {productsToShow.map((p, index) => {
                                const validation = productValidation[index] || {
                                  paymentTermValid: true,
                                  benefitTermValid: true,
                                };
                                const handleToggleSelect = (isChecking: boolean) => {
                                  if (readOnly) return;
                                  if (isChecking && selectedCount >= 6) {
                                    message.error("最多只能選擇6個產品");
                                    return;
                                  }
                                  // 更新产品选中状态
                                  const newSelection = [...(data.chooseProducts || [])];
                                  if (!newSelection[index]) newSelection[index] = newChooseProduct;
                                  newSelection[index] = {
                                    ...newSelection[index],
                                    choose: isChecking,
                                  };

                                  // 需求 1）：判断所有填写了产品计划的卡片是否来自同一家保险公司
                                  const productsWithCompany = newSelection.filter((p) => p.companyCode);
                                  const uniqueCompanies = new Set(productsWithCompany.map((p) => p.companyCode));
                                  const isSameCompany = productsWithCompany.length > 0 && uniqueCompanies.size === 1;

                                  // 自动设置 onlyProvideOneProduct
                                  const updatedData = {
                                    ...data,
                                    chooseProducts: newSelection,
                                    onlyProvideOneProduct: isSameCompany,
                                  };

                                  onChange(updatedData);
                                };

                                return (
                                  <Col span={24} key={index}>
                                    <CustomCard
                                      onClick={(e) => {
                                        // 检查点击的是否是 Checkbox 或其父元素
                                        const target = e.target as HTMLElement;
                                        if (target.closest(".ant-checkbox-wrapper") || target.closest(".ant-checkbox")) {
                                          return; // 如果点击的是 Checkbox，不处理 Card 的点击事件
                                        }
                                        handleToggleSelect(!p.choose);
                                      }}
                                      data-invalid={!validation.paymentTermValid || !validation.benefitTermValid}
                                      title={
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                          }}
                                        >
                                          <Checkbox
                                            checked={p.choose}
                                            disabled={readOnly}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              handleToggleSelect(e.target.checked);
                                            }}
                                          />
                                          <span>介紹的產品{index + 1}</span>
                                        </div>
                                      }
                                      extra={
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: 8,
                                            alignItems: "center",
                                          }}
                                        >
                                          {p.choose && (
                                            <CustomButton bgColor="var(--theme-primary)" height={24} fontSize={12}>
                                              已選擇
                                            </CustomButton>
                                          )}
                                          {(data.chooseProducts?.length || 0) > 1 && !readOnly && (
                                            <CustomButton
                                              bgColor="#ff4d4f"
                                              color="#fff"
                                              height={24}
                                              fontSize={12}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const newSelection = [...(data.chooseProducts || [])];
                                                newSelection.splice(index, 1);
                                                onChange({
                                                  ...data,
                                                  chooseProducts: newSelection,
                                                });
                                              }}
                                            >
                                              刪除
                                            </CustomButton>
                                          )}
                                        </div>
                                      }
                                      style={{
                                        border: p.choose ? "1px solid var(--theme-primary)" : "1px solid #d9d9d9",
                                      }}
                                    >
                                      <Row gutter={24}>
                                        <Col span={12}>
                                          <Form.Item label="產品計劃">
                                            {readOnly ? (
                                              <Input
                                                readOnly
                                                className={styles.zuuDerivedFieldInput}
                                                value={p.productName || ""}
                                                placeholder="請選擇"
                                                suffix={<DownOutlined className={styles.zuuDerivedFieldIcon} />}
                                              />
                                            ) : (
                                              <div onClick={(e) => e.stopPropagation()}>
                                                <ProductSelectInput value={p.productCode || ""} initialName={p.productName} context={{ type: "financial", index }} onOpenSelector={onOpenSelector} disabled={readOnly} />
                                              </div>
                                            )}
                                          </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                          <Form.Item label="選購產品目標">
                                            <Input disabled value={derived.objective} placeholder="自動生成" />
                                          </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                          <Form.Item label="選購產品的繳付年期">
                                            <Input disabled value={derived.paymentTerm} placeholder="自動生成" />
                                          </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                          <Form.Item label="目標得益/保障年期">
                                            <Input disabled value={derived.benefitTerm} placeholder="自動生成" />
                                          </Form.Item>
                                        </Col>
                                      </Row>

                                      {/* Validation Error Messages */}
                                      {/* {hasProduct && (!validation.paymentTermValid || !validation.benefitTermValid) && (
                                <div style={{ marginTop: 8, textAlign: "right" }}>
                                  {!validation.paymentTermValid && (
                                    <span style={{ color: "#ff4d4f", fontSize: 12, marginRight: 16 }}>
                                      繳付年期不符合
                                    </span>
                                  )}
                                  {!validation.benefitTermValid && (
                                    <span style={{ color: "#ff4d4f", fontSize: 14}}>
                                      保障期不符合
                                    </span>
                                  )}
                                </div>
                              )} */}
                                    </CustomCard>
                                  </Col>
                                );
                              })}
                            </>
                          );
                        })()}
                      </Row>

                      <Divider style={{ margin: "24px 0" }} />

                      {(() => {
                        // 需求 1）：当产品归属保险公司（去重后）数量=1时，展示原因字段
                        const allProducts = data.chooseProducts || [];
                        const productsWithCompany = allProducts.filter((p) => p.companyCode);

                        // 获取去重后的保险公司数量
                        const uniqueCompanies = new Set(productsWithCompany.map((p) => p.companyCode));
                        const shouldShowReason = productsWithCompany.length > 0 && uniqueCompanies.size === 1;

                        return shouldShowReason ? (
                          <Form.Item label="請在下方填寫只能提供一間保險公司建議之原因：" name="onlyProvideOneProductReason" rules={[{ required: true, message: "請輸入原因" }]}>
                            <Input placeholder="請輸入" value={data.onlyProvideOneProductReason} onChange={(e) => handleChange("onlyProvideOneProductReason", e.target.value)} />
                          </Form.Item>
                        ) : null;
                      })()}
                    </div>
                  </CustomCard>
                )}

                {/* 7. Suggestions and Reasons */}
                {!isZuuFinancialStepMode && (
                  <CustomCard title={isZuuFinancialStepMode ? "建議及原因" : "七、建議及原因"} className={cx(styles.sectionCard, isZuuFinancialStepMode && styles.zuuSectionCard)}>
                    <div style={{ marginBottom: 24, borderRadius: 6 }}>
                      <Form.Item label="1. 有沒有出現合適性偏差？" name="suitabilityDeviationOccurred" rules={[{ required: true }]}>
                        <Radio.Group
                          value={getQuestionValue("suggestionsAndReasons", "suitabilityDeviationOccurred", "RADIO")}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            const currentSuggestions = [...(data.suggestionsAndReasons || [])].filter((q) => q.question);

                            // 更新 suitabilityDeviationOccurred
                            const deviationIdx = currentSuggestions.findIndex((q) => q.question === "suitabilityDeviationOccurred");
                            const deviationItems = deviationIdx >= 0 ? currentSuggestions[deviationIdx].items || [] : [];
                            const updatedDeviationItems = deviationItems.map((i) => ({
                              ...i,
                              value: i.key === newValue,
                            }));
                            if (!updatedDeviationItems.find((i) => i.key === newValue)) {
                              updatedDeviationItems.push({ key: newValue, value: true });
                            }
                            if (deviationIdx >= 0) {
                              currentSuggestions[deviationIdx] = {
                                ...currentSuggestions[deviationIdx],
                                items: updatedDeviationItems,
                              };
                            } else {
                              currentSuggestions.push({
                                question: "suitabilityDeviationOccurred",
                                type: "RADIO",
                                items: updatedDeviationItems,
                              });
                            }

                            // 选择"有"(A) 时清空 7.3 (suitabilityDeviationFollowUpNoOptions)
                            // 选择"沒有"(B) 时清空 7.2 (suitabilityDeviationFollowUpYesOptions)、问题2和问题3
                            const clearKeys =
                              newValue === "A"
                                ? ["suitabilityDeviationFollowUpNoOptions"]
                                : ["suitabilityDeviationFollowUpYesOptions", "reasonForRecommendingDespiteDeviation", "otherReasonForRecommendation"];

                            clearKeys.forEach((key) => {
                              const idx = currentSuggestions.findIndex((q) => q.question === key);
                              if (idx >= 0) {
                                const isInput = key === "otherReasonForRecommendation";
                                currentSuggestions[idx] = {
                                  ...currentSuggestions[idx],
                                  items: isInput
                                    ? [{ key: "A", value: "" }]
                                    : (currentSuggestions[idx].items || []).map((i) => ({
                                        ...i,
                                        value: false,
                                      })),
                                };
                              }
                              form.setFieldValue(key, undefined);
                            });

                            onChange({ ...data, suggestionsAndReasons: currentSuggestions });
                          }}
                        >
                          <Radio value="A">有</Radio>
                          <Radio value="B">沒有</Radio>
                        </Radio.Group>
                      </Form.Item>

                      {getQuestionValue("suggestionsAndReasons", "suitabilityDeviationOccurred", "RADIO") === "A" && (
                        <div style={{ marginTop: 16 }}>
                          <Form.Item label="1.1 若上方問題之答案為「有」者，請選擇原因，（勾選一項）：" name="suitabilityDeviationFollowUpYesOptions" rules={[{ required: true }]}>
                            <Radio.Group
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                              }}
                              value={getQuestionValue("suggestionsAndReasons", "suitabilityDeviationFollowUpYesOptions", "RADIO")}
                              onChange={(e) => updateQuestionValue("suggestionsAndReasons", "suitabilityDeviationFollowUpYesOptions", e.target.value, "RADIO")}
                            >
                              <Radio value="A">a. 購買保險產品的目標</Radio>
                              <Radio value="B">b. 目標利益/保障期的預期時間</Radio>
                              <Radio value="C">c. 能夠及願意繳付的保費佔所有收入來源</Radio>
                              <Radio value="D">d. 能夠及願意為保單支付保費的年期</Radio>
                              <Radio value="E">e. 保障所需要的金額</Radio>
                              <Radio
                                value="F"
                                style={{
                                  whiteSpace: "normal",
                                  alignItems: "flex-start",
                                }}
                              >
                                f. 此建議是考慮到申請人的理財目標、全面保障的需要、各需要的優先重要性及申請人的財務預算而作出，在與申請人商討後，申請人期望在以上各方面及以下原因取得平衡。
                              </Radio>
                            </Radio.Group>
                          </Form.Item>
                        </div>
                      )}

                      {getQuestionValue("suggestionsAndReasons", "suitabilityDeviationOccurred", "RADIO") === "B" && (
                        <div style={{ marginTop: 16 }}>
                          <Form.Item label="1.2 若上方問題之答案為「沒有」者，請選擇原因，（勾選一項）：" name="suitabilityDeviationFollowUpNoOptions" rules={[{ required: true }]}>
                            <Radio.Group
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                              }}
                              value={getQuestionValue("suggestionsAndReasons", "suitabilityDeviationFollowUpNoOptions", "RADIO")}
                              onChange={(e) => updateQuestionValue("suggestionsAndReasons", "suitabilityDeviationFollowUpNoOptions", e.target.value, "RADIO")}
                            >
                              <Radio
                                value="A"
                                style={{
                                  whiteSpace: "normal",
                                  alignItems: "flex-start",
                                }}
                              >
                                a. 此建議是考慮到申請人的理財目標、全面保障的需要、各需要的優先重要性及申請人的財務預算而作出
                              </Radio>
                              <Radio
                                value="B"
                                style={{
                                  whiteSpace: "normal",
                                  alignItems: "flex-start",
                                }}
                              >
                                b. 此建議是考慮到申請人的理財目標、全面保障的需要、各需要的優先重要性及申請人的財務預算而作出，在與申請人商討後，申請人期望在各方面取得平衡
                              </Radio>
                            </Radio.Group>
                          </Form.Item>
                        </div>
                      )}

                      {getQuestionValue("suggestionsAndReasons", "suitabilityDeviationOccurred", "RADIO") === "A" && (
                        <Form.Item label="2. 雖然有合適性偏差出現，但仍然介紹此產品的原因是：（勾選一項）" name="reasonForRecommendingDespiteDeviation" rules={[{ required: true }]}>
                          <Radio.Group
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                            value={getQuestionValue("suggestionsAndReasons", "reasonForRecommendingDespiteDeviation", "RADIO")}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              const currentSuggestions = [...(data.suggestionsAndReasons || [])].filter((q) => q.question);

                              // 更新 reasonForRecommendingDespiteDeviation
                              const recIdx = currentSuggestions.findIndex((q) => q.question === "reasonForRecommendingDespiteDeviation");
                              const recItems = recIdx >= 0 ? currentSuggestions[recIdx].items || [] : [];
                              const updatedRecItems = recItems.map((i) => ({
                                ...i,
                                value: i.key === newValue,
                              }));
                              if (!updatedRecItems.find((i) => i.key === newValue)) {
                                updatedRecItems.push({ key: newValue, value: true });
                              }
                              if (recIdx >= 0) {
                                currentSuggestions[recIdx] = {
                                  ...currentSuggestions[recIdx],
                                  items: updatedRecItems,
                                };
                              } else {
                                currentSuggestions.push({
                                  question: "reasonForRecommendingDespiteDeviation",
                                  type: "RADIO",
                                  items: updatedRecItems,
                                });
                              }

                              // 当选择非 B 时,清空 otherReasonForRecommendation
                              if (newValue !== "B") {
                                const otherIdx = currentSuggestions.findIndex((q) => q.question === "otherReasonForRecommendation");
                                if (otherIdx >= 0) {
                                  currentSuggestions[otherIdx] = {
                                    ...currentSuggestions[otherIdx],
                                    items: [{ key: "A", value: "" }],
                                  };
                                }
                                form.setFieldValue("otherReasonForRecommendation", undefined);
                              }

                              onChange({ ...data, suggestionsAndReasons: currentSuggestions });
                            }}
                          >
                            <Radio
                              value="A"
                              style={{
                                whiteSpace: "normal",
                                alignItems: "flex-start",
                              }}
                            >
                              A. 因現在供款預算原因，這保險計劃的金額未能達到保險或儲蓄的全面需求，但現在建議的保險，已經符合年期及供款金額等要求，能夠邁向至全面的保障及儲蓄目標
                            </Radio>
                            <Radio value="B">其他</Radio>
                          </Radio.Group>
                        </Form.Item>
                      )}

                      {getQuestionValue("suggestionsAndReasons", "suitabilityDeviationOccurred", "RADIO") === "A" &&
                        getQuestionValue("suggestionsAndReasons", "reasonForRecommendingDespiteDeviation", "RADIO") === "B" && (
                          <div style={{ marginTop: 16 }}>
                            <Form.Item label="3. 其他原因" name="otherReasonForRecommendation" rules={[{ required: true, message: "請輸入原因" }]}>
                              <Input
                                value={getQuestionValue("suggestionsAndReasons", "otherReasonForRecommendation", "INPUT")}
                                onChange={(e) => updateQuestionValue("suggestionsAndReasons", "otherReasonForRecommendation", e.target.value, "INPUT")}
                                placeholder="請輸入"
                              />
                            </Form.Item>
                          </div>
                        )}
                    </div>
                  </CustomCard>
                )}
              </>
            </FnaFormComponent>
          </>
        )}
        {/* </>
        )} */}
      </div>
    );
  },
);
